/*
 * SongShake FX engine: plain JavaScript, no dependencies, no image files. Owned and maintained by
 * SongShake (ENG-871). Draws SongShake vibe sets (energy build + afterglow), boost animations and
 * the +1 cherry on two <canvas> layers around the album cards, and runs the standard handoff
 * between them. Every package (vibe set, boost animation, effect rules) uses this same file.
 *
 * The backend mirrors parts of it: fx/crowd.go ports `energy` (needs, support, crowdLevels) and
 * fx/rules.go falls back to the rules defaults. defaults.json and crowd-golden.json are synced to
 * the backend with `npm run fx:sync-defaults`; change them here, then sync.
 */
import DEFAULTS_JSON from './defaults.json';

const SongShakeFX = (function () {
  'use strict';

  /* ===================== small helpers ===================== */
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const easeIn = (t) => t * t * t;
  const smooth = (a, b, x) => {
    const t = clamp((x - a) / (b - a || 1e-6), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const TAU = Math.PI * 2;
  const hh = (a) => {
    const s = Math.sin(a * 12.9898 + 78.233) * 43758.5453;
    return s - Math.floor(s);
  };
  function hexToRgb(h) {
    h = String(h).replace('#', '');
    if (h.length === 3)
      h = h
        .split('')
        .map((c) => c + c)
        .join('');
    const n = parseInt(h, 16) || 0;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const mix = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
  const rgba = (c, a) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${clamp(a, 0, 1).toFixed(3)})`;
  const isHex = (x) => typeof x === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(x);

  /* Per-frame drawing context (set by the stage that is drawing). */
  let DPR = 1,
    NOW = 0,
    SW = 0,
    SH = 0,
    STRIP = 0;

  /* Soft round glow, painted in code onto a small off-screen canvas (no image file). */
  const SPR = new Map();
  function sprite(c) {
    const key = ((c[0] >> 3) << 10) | ((c[1] >> 3) << 5) | (c[2] >> 3);
    let s = SPR.get(key);
    if (!s) {
      s = document.createElement('canvas');
      s.width = s.height = 32;
      const g = s.getContext('2d');
      const gr = g.createRadialGradient(16, 16, 0, 16, 16, 16);
      const b = `${c[0] | 0},${c[1] | 0},${c[2] | 0}`;
      gr.addColorStop(0, `rgba(${b},1)`);
      gr.addColorStop(0.3, `rgba(${b},.6)`);
      gr.addColorStop(1, `rgba(${b},0)`);
      g.fillStyle = gr;
      g.fillRect(0, 0, 32, 32);
      SPR.set(key, s);
    }
    return s;
  }
  function dsp(ctx, x, y, rad, c, al) {
    if (al <= 0.004 || rad <= 0.2) return;
    ctx.globalAlpha = Math.min(1, al);
    ctx.drawImage(sprite(c), x - rad, y - rad, rad * 2, rad * 2);
    ctx.globalAlpha = 1;
  }

  /* ===================== settings ===================== */
  /* Default settings for each package type: defaults.json, the single source of truth. The backend
     keeps a synced copy (npm run fx:sync-defaults). Copied so setDefaults never edits the import. */
  const DEFAULTS = JSON.parse(JSON.stringify(DEFAULTS_JSON));
  const EASES = {
    spring: 'cubic-bezier(.34,1.45,.5,1)',
    smooth: 'cubic-bezier(.22,1,.36,1)',
    snap: 'cubic-bezier(.75,0,.15,1)',
    linear: 'linear',
  };

  /* Turn a settings object into a fast lookup: colors become [r,g,b], 5-value arrays stay keyframes. */
  function compile(values) {
    const v = Object.assign({}, values),
      col = {},
      cc = {},
      look = [],
      ag = [];
    for (const k in v) {
      const x = v[k];
      if (isHex(x)) col[k] = hexToRgb(x);
      else if (Array.isArray(x) && x.length === 5 && isHex(x[0])) cc[k] = x.map(hexToRgb);
      if (k.startsWith('ag_')) ag.push(k);
      else look.push(k);
    }
    return { v, col, cc, look, ag };
  }
  /* Evaluate a look at display level D (0 = Start … 1 = Peak) by blending the two nearest of the 5 keyframes. */
  function evalLook(C, D) {
    const x = clamp(D, 0, 1) * 4,
      i = Math.min(3, Math.floor(x)),
      f = x - i,
      S = {},
      v = C.v;
    for (const k of C.look) {
      const a = v[k];
      if (C.cc[k]) S[k] = mix(C.cc[k][i], C.cc[k][i + 1], f);
      else if (Array.isArray(a) && a.length === 5) S[k] = a[i] + (a[i + 1] - a[i]) * f;
      else S[k] = a;
    }
    return S;
  }
  /* Evaluate the afterglow look between keyframes i and j (0–2 = small/medium/big top, 3 = Settling, 4 = Settled). */
  function evalBetween(C, i, j, f) {
    const S = {},
      v = C.v;
    for (const k of C.ag) {
      const b = k.slice(3),
        a = v[k];
      if (C.cc[k]) S[b] = mix(C.cc[k][i], C.cc[k][j], f);
      else if (Array.isArray(a) && a.length === 5) S[b] = a[i] + (a[j] - a[i]) * f;
      else S[b] = a;
    }
    return S;
  }

  /* ===================== packages ===================== */
  const REGISTRY = {};
  const TYPES = ['vibe', 'boost', 'rules'];
  const typeOf = (t) => (t === 'platform' ? 'rules' : t);
  function registerPackage(pkg) {
    if (pkg) pkg.type = typeOf(pkg.type);
    if (!pkg || TYPES.indexOf(pkg.type) < 0 || !pkg.id)
      throw new Error(
        'SongShakeFX.registerPackage: needs {type: "vibe"|"boost"|"rules", id, settings}',
      );
    if (pkg.shapes)
      for (const id in pkg.shapes) {
        const s = pkg.shapes[id];
        if (s && s.svg) registerShape(id, s);
      }
    REGISTRY[pkg.id] = pkg;
    return pkg;
  }
  function getPackage(id) {
    return REGISTRY[id] || null;
  }
  /* Accepts a registered package, a package id, or a plain settings object. Missing keys fall back to the defaults. */
  function settingsOf(x, type) {
    type = typeOf(type);
    let vals = x;
    if (typeof x === 'string') {
      const p = REGISTRY[x];
      if (!p) throw new Error('SongShakeFX: no package registered with id "' + x + '"');
      vals = p.settings;
    } else if (x && x.settings && x.type) vals = x.settings;
    return Object.assign({}, DEFAULTS[type] || {}, vals || {});
  }
  const compileAs = (x, type) => compile(settingsOf(x, type));

  /* ===================== geometry ===================== */
  const cardRR = (r, o = 0) => ({
    x: r.x - o,
    y: r.y - o,
    w: r.w + 2 * o,
    h: r.h + 2 * o,
    r: Math.max(0, 10 + o),
  });
  function rrLen(R) {
    const r = Math.min(R.r, R.w / 2, R.h / 2);
    return 2 * (R.w - 2 * r) + 2 * (R.h - 2 * r) + TAU * r;
  }
  function rrPoint(R, s) {
    const r = Math.min(R.r, R.w / 2, R.h / 2),
      w = R.w - 2 * r,
      h = R.h - 2 * r,
      q = (Math.PI / 2) * r,
      L = 2 * w + 2 * h + 4 * q,
      rd = Math.max(r, 1e-6);
    s = ((s % L) + L) % L;
    const arc = (cx, cy, a) => ({
      x: cx + Math.cos(a) * r,
      y: cy + Math.sin(a) * r,
      nx: Math.cos(a),
      ny: Math.sin(a),
    });
    if (s < w) return { x: R.x + r + s, y: R.y, nx: 0, ny: -1 };
    s -= w;
    if (s < q) return arc(R.x + r + w, R.y + r, -Math.PI / 2 + s / rd);
    s -= q;
    if (s < h) return { x: R.x + R.w, y: R.y + r + s, nx: 1, ny: 0 };
    s -= h;
    if (s < q) return arc(R.x + r + w, R.y + r + h, s / rd);
    s -= q;
    if (s < w) return { x: R.x + r + w - s, y: R.y + R.h, nx: 0, ny: 1 };
    s -= w;
    if (s < q) return arc(R.x + r, R.y + r + h, Math.PI / 2 + s / rd);
    s -= q;
    if (s < h) return { x: R.x, y: R.y + r + h - s, nx: -1, ny: 0 };
    s -= h;
    return arc(R.x + r, R.y + r, Math.PI + s / rd);
  }
  function rr(ctx, x, y, w, h, r) {
    ctx.beginPath();
    addRR(ctx, x, y, w, h, r);
  }
  function addRR(ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
  function clipStrip(ctx) {
    ctx.beginPath();
    ctx.rect(0, STRIP, SW, Math.max(0, SH - STRIP));
    ctx.clip();
  }
  function clipTo(ctx, C) {
    clipStrip(ctx);
    rr(ctx, C.x, C.y, C.w, C.h, C.r);
    ctx.clip();
  }
  function edgeFade(r, m, x, y) {
    const dx = Math.max(r.x - x, 0, x - (r.x + r.w)),
      dy = Math.max(r.y - y, 0, y - (r.y + r.h));
    return 1 - smooth(m * 0.55, m, Math.hypot(dx, dy));
  }
  function glow(ctx, r, color, alpha, blur) {
    if (alpha <= 0.003 || blur <= 0) return;
    ctx.save();
    ctx.shadowColor = rgba(color, alpha);
    ctx.shadowBlur = blur * DPR;
    ctx.fillStyle = rgba(color, alpha);
    rr(ctx, r.x, r.y, r.w, r.h, 10);
    ctx.fill();
    ctx.restore();
  }

  /* ===================== SVG drawn in code ===================== */
  /* A small SVG painter: reads SVG markup (text written in the code) and draws its shapes with Canvas 2D.
   Nothing is loaded as an image. Supports path, circle, ellipse, rect, line, polyline, polygon, g, transforms,
   fill/stroke (incl. opacity, dashes, caps, joins) and simple linear/radial gradients. */
  function parseStyle(el, inh) {
    const o = Object.assign({}, inh);
    const set = (k, v) => {
      if (v == null || v === '') return;
      v = String(v).trim();
      switch (k) {
        case 'fill':
          o.fill = v;
          break;
        case 'stroke':
          o.stroke = v;
          break;
        case 'stroke-width':
          o.sw = parseFloat(v);
          break;
        case 'opacity':
          o.op = parseFloat(v);
          break;
        case 'fill-opacity':
          o.fop = parseFloat(v);
          break;
        case 'stroke-opacity':
          o.sop = parseFloat(v);
          break;
        case 'stroke-linecap':
          o.cap = v;
          break;
        case 'stroke-linejoin':
          o.join = v;
          break;
        case 'stroke-dasharray':
          o.dash =
            v === 'none'
              ? null
              : v
                  .split(/[\s,]+/)
                  .map(Number)
                  .filter((n) => !isNaN(n));
          break;
        case 'stroke-miterlimit':
          o.miter = parseFloat(v);
          break;
        case 'fill-rule':
          o.rule = v;
          break;
        case 'color':
          o.color = v;
          break;
        case 'display':
          o.display = v;
          break;
        case 'visibility':
          o.vis = v;
          break;
      }
    };
    [
      'fill',
      'stroke',
      'stroke-width',
      'fill-opacity',
      'stroke-opacity',
      'stroke-linecap',
      'stroke-linejoin',
      'stroke-dasharray',
      'stroke-miterlimit',
      'fill-rule',
      'color',
      'display',
      'visibility',
    ].forEach((a) => set(a, el.getAttribute(a)));
    o.op = 1;
    set('opacity', el.getAttribute('opacity'));
    const st = el.getAttribute('style');
    if (st)
      st.split(';').forEach((d) => {
        const i = d.indexOf(':');
        if (i > 0) set(d.slice(0, i).trim(), d.slice(i + 1));
      });
    return o;
  }
  function applyTransform(ctx, t) {
    if (!t) return;
    const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
    let m;
    while ((m = re.exec(t))) {
      const a = m[2]
        .split(/[\s,]+/)
        .filter(Boolean)
        .map(Number);
      switch (m[1]) {
        case 'matrix':
          ctx.transform(a[0], a[1], a[2], a[3], a[4], a[5]);
          break;
        case 'translate':
          ctx.translate(a[0] || 0, a[1] || 0);
          break;
        case 'scale':
          ctx.scale(a[0], a.length > 1 ? a[1] : a[0]);
          break;
        case 'rotate':
          if (a.length > 2) {
            ctx.translate(a[1], a[2]);
            ctx.rotate((a[0] * Math.PI) / 180);
            ctx.translate(-a[1], -a[2]);
          } else ctx.rotate((a[0] * Math.PI) / 180);
          break;
        case 'skewX':
          ctx.transform(1, 0, Math.tan((a[0] * Math.PI) / 180), 1, 0, 0);
          break;
        case 'skewY':
          ctx.transform(1, Math.tan((a[0] * Math.PI) / 180), 0, 1, 0, 0);
          break;
      }
    }
  }
  const num = (el, a, d = 0) => {
    const v = parseFloat(el.getAttribute(a));
    return isNaN(v) ? d : v;
  };
  function pointsOf(el) {
    return (el.getAttribute('points') || '')
      .trim()
      .split(/[\s,]+/)
      .map(Number)
      .filter((n) => !isNaN(n));
  }
  function geomOf(el, tag) {
    const p = new Path2D();
    let bb = null;
    if (tag === 'path') {
      const d = el.getAttribute('d') || '';
      try {
        p.addPath(new Path2D(d));
      } catch (e) {}
      const n = (d.match(/-?\d*\.?\d+(?:e-?\d+)?/gi) || []).map(Number);
      if (n.length >= 2) {
        let x0 = 1e9,
          y0 = 1e9,
          x1 = -1e9,
          y1 = -1e9;
        for (let i = 0; i + 1 < n.length; i += 2) {
          x0 = Math.min(x0, n[i]);
          x1 = Math.max(x1, n[i]);
          y0 = Math.min(y0, n[i + 1]);
          y1 = Math.max(y1, n[i + 1]);
        }
        bb = [x0, y0, x1 - x0, y1 - y0];
      }
    } else if (tag === 'circle') {
      const cx = num(el, 'cx'),
        cy = num(el, 'cy'),
        r = num(el, 'r');
      p.arc(cx, cy, r, 0, TAU);
      bb = [cx - r, cy - r, 2 * r, 2 * r];
    } else if (tag === 'ellipse') {
      const cx = num(el, 'cx'),
        cy = num(el, 'cy'),
        rx = num(el, 'rx'),
        ry = num(el, 'ry');
      p.ellipse(cx, cy, rx, ry, 0, 0, TAU);
      bb = [cx - rx, cy - ry, 2 * rx, 2 * ry];
    } else if (tag === 'rect') {
      const x = num(el, 'x'),
        y = num(el, 'y'),
        w = num(el, 'width'),
        h = num(el, 'height');
      let rx = num(el, 'rx', NaN),
        ry = num(el, 'ry', NaN);
      if (isNaN(rx)) rx = isNaN(ry) ? 0 : ry;
      if (isNaN(ry)) ry = rx;
      rx = Math.min(rx, w / 2);
      ry = Math.min(ry, h / 2);
      if (rx > 0 || ry > 0) {
        p.moveTo(x + rx, y);
        p.lineTo(x + w - rx, y);
        p.ellipse(x + w - rx, y + ry, rx, ry, 0, -Math.PI / 2, 0);
        p.lineTo(x + w, y + h - ry);
        p.ellipse(x + w - rx, y + h - ry, rx, ry, 0, 0, Math.PI / 2);
        p.lineTo(x + rx, y + h);
        p.ellipse(x + rx, y + h - ry, rx, ry, 0, Math.PI / 2, Math.PI);
        p.lineTo(x, y + ry);
        p.ellipse(x + rx, y + ry, rx, ry, 0, Math.PI, Math.PI * 1.5);
        p.closePath();
      } else p.rect(x, y, w, h);
      bb = [x, y, w, h];
    } else if (tag === 'line') {
      const x1 = num(el, 'x1'),
        y1 = num(el, 'y1'),
        x2 = num(el, 'x2'),
        y2 = num(el, 'y2');
      p.moveTo(x1, y1);
      p.lineTo(x2, y2);
      bb = [Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1)];
    } else if (tag === 'polyline' || tag === 'polygon') {
      const a = pointsOf(el);
      let x0 = 1e9,
        y0 = 1e9,
        x1 = -1e9,
        y1 = -1e9;
      for (let i = 0; i + 1 < a.length; i += 2) {
        i ? p.lineTo(a[i], a[i + 1]) : p.moveTo(a[i], a[i + 1]);
        x0 = Math.min(x0, a[i]);
        x1 = Math.max(x1, a[i]);
        y0 = Math.min(y0, a[i + 1]);
        y1 = Math.max(y1, a[i + 1]);
      }
      if (tag === 'polygon') p.closePath();
      bb = [x0, y0, x1 - x0, y1 - y0];
    }
    return { p, bb };
  }
  function paintOf(ctx, val, ids, bb, inh, rep) {
    if (!val || val === 'none') return null;
    if (val === 'currentColor') return inh.color || '#000';
    const m = /^url\(\s*#([^)\s]+)\s*\)/.exec(val);
    if (!m) return val;
    const g = ids[m[1]];
    if (!g) {
      rep.warn.add('missing paint');
      return null;
    }
    const stops = [...g.querySelectorAll('stop')].map((s) => {
      const st = parseStyle(s, {});
      const sc =
        s.getAttribute('stop-color') ||
        (/stop-color\s*:\s*([^;]+)/.exec(s.getAttribute('style') || '') || [])[1] ||
        '#000';
      const so = parseFloat(
        s.getAttribute('stop-opacity') ||
          (/stop-opacity\s*:\s*([^;]+)/.exec(s.getAttribute('style') || '') || [])[1] ||
          '1',
      );
      let off = s.getAttribute('offset') || '0';
      off = off.endsWith('%') ? parseFloat(off) / 100 : parseFloat(off);
      return { off: clamp(off || 0, 0, 1), c: sc, o: isNaN(so) ? 1 : so };
    });
    if (!stops.length) return null;
    const tag = g.nodeName.toLowerCase(),
      user = g.getAttribute('gradientUnits') === 'userSpaceOnUse';
    const b = bb || [0, 0, 1, 1],
      P = (v, d, axis) => {
        if (v == null) return d;
        const pct = String(v).endsWith('%'),
          n = parseFloat(v);
        if (user) return pct ? (n / 100) * (axis ? b[3] : b[2]) : n;
        const f = pct ? n / 100 : n;
        return axis ? b[1] + f * b[3] : b[0] + f * b[2];
      };
    let gr;
    try {
      if (tag === 'lineargradient')
        gr = ctx.createLinearGradient(
          P(g.getAttribute('x1'), user ? b[0] : b[0], 0),
          P(g.getAttribute('y1'), user ? b[1] : b[1], 1),
          P(g.getAttribute('x2'), user ? b[0] + b[2] : b[0] + b[2], 0),
          P(g.getAttribute('y2'), user ? b[1] : b[1], 1),
        );
      else {
        const cx = P(g.getAttribute('cx'), b[0] + b[2] / 2, 0),
          cy = P(g.getAttribute('cy'), b[1] + b[3] / 2, 1);
        let r = g.getAttribute('r');
        r =
          r == null
            ? Math.max(b[2], b[3]) / 2
            : String(r).endsWith('%')
              ? (parseFloat(r) / 100) * Math.max(b[2], b[3])
              : user
                ? parseFloat(r)
                : parseFloat(r) * Math.max(b[2], b[3]);
        gr = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(0.01, r));
      }
      for (const s of stops) {
        ctx.fillStyle = s.c;
        const c = ctx.fillStyle;
        let rgb = c.startsWith('#')
          ? hexToRgb(c)
          : c
              .match(/\d+(\.\d+)?/g)
              .slice(0, 3)
              .map(Number);
        gr.addColorStop(s.off, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${s.o})`);
      }
    } catch (e) {
      rep.warn.add('gradient simplified');
      return stops[0].c;
    }
    return gr;
  }
  const SKIP = {
    defs: 1,
    title: 1,
    desc: 1,
    metadata: 1,
    lineargradient: 1,
    radialgradient: 1,
    stop: 1,
  };
  const UNSUPPORTED = {
    text: 'text',
    image: 'embedded image',
    use: '<use> references',
    filter: 'filters',
    mask: 'masks',
    clippath: 'clip paths',
    pattern: 'patterns',
    foreignobject: 'foreign content',
    symbol: 'symbols',
    script: 'scripts',
    style: 'CSS <style> blocks (use attributes instead)',
  };
  function drawSVGNode(ctx, el, inh, ids, rep) {
    for (const ch of el.children) {
      const tag = ch.nodeName.toLowerCase();
      if (SKIP[tag]) continue;
      if (UNSUPPORTED[tag]) {
        rep.unsupported.add(UNSUPPORTED[tag]);
        if (tag === 'image') rep.raster = true;
        continue;
      }
      const st = parseStyle(ch, inh);
      if (st.display === 'none' || st.vis === 'hidden') continue;
      ctx.save();
      applyTransform(ctx, ch.getAttribute('transform'));
      const base = ctx.globalAlpha * (isNaN(st.op) ? 1 : st.op);
      ctx.globalAlpha = base;
      if (tag === 'g' || tag === 'svg' || tag === 'a' || tag === 'switch') {
        drawSVGNode(ctx, ch, st, ids, rep);
      } else if (/^(path|circle|ellipse|rect|line|polyline|polygon)$/.test(tag)) {
        const { p, bb } = geomOf(ch, tag);
        if (tag !== 'line') {
          const f = paintOf(ctx, st.fill, ids, bb, st, rep);
          if (f) {
            ctx.globalAlpha = base * (isNaN(st.fop) ? 1 : st.fop);
            ctx.fillStyle = f;
            ctx.fill(p, st.rule === 'evenodd' ? 'evenodd' : 'nonzero');
          }
        }
        const s = paintOf(ctx, st.stroke, ids, bb, st, rep);
        if (s && st.sw > 0) {
          ctx.globalAlpha = base * (isNaN(st.sop) ? 1 : st.sop);
          ctx.strokeStyle = s;
          ctx.lineWidth = st.sw;
          ctx.lineCap = st.cap || 'butt';
          ctx.lineJoin = st.join || 'miter';
          if (st.miter) ctx.miterLimit = st.miter;
          ctx.setLineDash(st.dash || []);
          ctx.stroke(p);
        }
      } else rep.unsupported.add('<' + tag + '>');
      ctx.restore();
    }
  }
  /* Render SVG markup into a square canvas of `size` px. Returns {canvas, report}. */
  function renderSVG(src, size) {
    const rep = {
      unsupported: new Set(),
      warn: new Set(),
      raster: /<image[\s>]|data:image\/(png|jpe?g|gif|webp)/i.test(src),
      external: /(href|src)\s*=\s*["']\s*(https?:|\/\/)/i.test(src),
    };
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    let doc;
    try {
      doc = new DOMParser().parseFromString(src, 'image/svg+xml');
    } catch (e) {
      rep.error = 'Not valid SVG';
      return { canvas: c, report: rep };
    }
    const svg = doc.documentElement;
    if (
      !svg ||
      svg.nodeName.toLowerCase() !== 'svg' ||
      doc.getElementsByTagName('parsererror').length
    ) {
      rep.error = 'Not valid SVG';
      return { canvas: c, report: rep };
    }
    let vb = (svg.getAttribute('viewBox') || '')
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (vb.length !== 4 || vb.some(isNaN) || vb[2] <= 0 || vb[3] <= 0) {
      const w = parseFloat(svg.getAttribute('width')) || 64,
        h = parseFloat(svg.getAttribute('height')) || 64;
      vb = [0, 0, w, h];
    }
    const s = size / Math.max(vb[2], vb[3]);
    ctx.translate((size - vb[2] * s) / 2, (size - vb[3] * s) / 2);
    ctx.scale(s, s);
    ctx.translate(-vb[0], -vb[1]);
    const ids = {};
    doc.querySelectorAll('[id]').forEach((e) => (ids[e.getAttribute('id')] = e));
    drawSVGNode(
      ctx,
      svg,
      { fill: '#000', stroke: 'none', sw: 1, op: 1, fop: 1, sop: 1, color: '#000' },
      ids,
      rep,
    );
    return { canvas: c, report: rep };
  }

  /* ===================== shapes ===================== */
  const SHAPE_GROUPS = [
    'Basic',
    'SongShake',
    'Halloween',
    'Christmas',
    'Fall',
    'Summer',
    'Easter',
    'Sports',
    'Party',
  ];
  const SV = (b) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">${b}</svg>`;
  const arm = `<path d="M32 32V6M25 11l7 7 7-7M26 21l6 5 6-5" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`;
  const SHAPE_DEFS = [
    { id: 'spark', l: 'Spark streak', g: 'Basic', kind: 'special' },
    { id: 'puffs', l: 'Soft puff', g: 'Basic', kind: 'special' },
    {
      id: 'circles',
      l: 'Circle',
      g: 'Basic',
      kind: 'white',
      svg: SV(`<circle cx="32" cy="32" r="28" fill="#fff"/>`),
    },
    {
      id: 'triangles',
      l: 'Triangle',
      g: 'Basic',
      kind: 'white',
      svg: SV(`<polygon points="32,5 60,55 4,55" fill="#fff"/>`),
    },
    {
      id: 'stars',
      l: 'Star',
      g: 'Basic',
      kind: 'white',
      svg: SV(
        `<polygon points="32,3 40,23 61,24 45,37 50,58 32,46 14,58 19,37 3,24 24,23" fill="#fff"/>`,
      ),
    },
    {
      id: 'rings',
      l: 'Ring',
      g: 'Basic',
      kind: 'white',
      svg: SV(`<circle cx="32" cy="32" r="24" fill="none" stroke="#fff" stroke-width="8"/>`),
    },
    {
      id: 'diamond',
      l: 'Diamond',
      g: 'Basic',
      kind: 'white',
      svg: SV(`<polygon points="32,3 56,32 32,61 8,32" fill="#fff"/>`),
    },
    {
      id: 'sparkle',
      l: 'Sparkle',
      g: 'Basic',
      kind: 'white',
      svg: SV(`<path d="M32 2Q36 28 62 32Q36 36 32 62Q28 36 2 32Q28 28 32 2Z" fill="#fff"/>`),
    },
    {
      id: 'heartw',
      l: 'Heart (solid)',
      g: 'Basic',
      kind: 'white',
      svg: SV(
        `<path d="M32 57C10 41 3 29 9 18 15 8 28 9 32 20 36 9 49 8 55 18 61 29 54 41 32 57Z" fill="#fff"/>`,
      ),
    },
    {
      id: 'cherry',
      l: 'Cherries',
      g: 'SongShake',
      kind: 'color',
      svg: SV(
        `<path d="M22 42Q26 20 40 7M44 44Q41 24 40 7" stroke="#3f7a2a" stroke-width="3" fill="none" stroke-linecap="round"/><path d="M40 7Q53 2 57 13Q46 16 40 7Z" fill="#5fa83a"/><circle cx="21" cy="46" r="13" fill="#c80d16"/><circle cx="44" cy="48" r="13" fill="#e8141f"/><circle cx="16" cy="41" r="3.5" fill="#ff9a9a"/><circle cx="39" cy="43" r="3.5" fill="#ff9a9a"/>`,
      ),
    },
    {
      id: 'cherry1',
      l: 'Cherry (logo)',
      g: 'SongShake',
      kind: 'color',
      svg: SV(
        `<path d="M31 24Q30 11 45 3" stroke="#4f9a2e" stroke-width="3.5" fill="none" stroke-linecap="round"/><path d="M31 22C23 16 6 19 6 38 6 53 18 61 31 61 44 61 56 53 56 38 56 19 39 16 31 22Z" fill="#e8141f"/><ellipse cx="17" cy="33" rx="3.6" ry="7" fill="#ff8a8a" transform="rotate(25 17 33)"/>`,
      ),
    },
    {
      id: 'sprinkle',
      l: 'Sprinkles (5 colors)',
      g: 'SongShake',
      kind: 'white',
      multi: 'sprinkle',
      svg: SV(`<rect x="26" y="6" width="12" height="52" rx="6" fill="#fff"/>`),
    },
    {
      id: 'milkshake',
      l: 'Milkshake',
      g: 'SongShake',
      kind: 'color',
      svg: SV(
        `<rect x="38" y="1" width="5" height="26" rx="2" fill="#f70e12" transform="rotate(18 40 14)"/><path d="M17 27H47L41 61H23Z" fill="#ff8fb8"/><rect x="22" y="31" width="3" height="25" fill="#ffd3e4"/><path d="M13 28Q13 18 21 19Q24 11 32 13Q40 9 43 18Q51 18 51 28Z" fill="#fff6ea"/><circle cx="32" cy="10" r="5.5" fill="#e8141f"/>`,
      ),
    },
    {
      id: 'note',
      l: 'Music note',
      g: 'SongShake',
      kind: 'white',
      svg: SV(
        `<path d="M24 47V13L50 7V41" stroke="#fff" stroke-width="5" fill="none" stroke-linejoin="round"/><ellipse cx="17" cy="48" rx="9" ry="7" fill="#fff"/><ellipse cx="43" cy="42" rx="9" ry="7" fill="#fff"/>`,
      ),
    },
    {
      id: 'vinyl',
      l: 'Vinyl record',
      g: 'SongShake',
      kind: 'color',
      svg: SV(
        `<circle cx="32" cy="32" r="29" fill="#1b1b1f"/><circle cx="32" cy="32" r="23" fill="none" stroke="#3a3a42" stroke-width="1.5"/><circle cx="32" cy="32" r="17" fill="none" stroke="#3a3a42" stroke-width="1.5"/><circle cx="32" cy="32" r="10" fill="#f70e12"/><circle cx="32" cy="32" r="2" fill="#1b1b1f"/>`,
      ),
    },
    {
      id: 'heart',
      l: 'Heart',
      g: 'SongShake',
      kind: 'color',
      svg: SV(
        `<path d="M32 57C10 41 3 29 9 18 15 8 28 9 32 20 36 9 49 8 55 18 61 29 54 41 32 57Z" fill="#f70e12"/><path d="M15 20Q18 14 24 15" stroke="#ff9a9a" stroke-width="3" fill="none" stroke-linecap="round"/>`,
      ),
    },
    {
      id: 'pumpkin',
      l: 'Pumpkin',
      g: 'Halloween',
      kind: 'color',
      svg: SV(
        `<path d="M30 21Q30 12 37 7L40 10Q35 14 35 21Z" fill="#4f7a2a"/><ellipse cx="20" cy="39" rx="14" ry="18" fill="#e06f18"/><ellipse cx="44" cy="39" rx="14" ry="18" fill="#e06f18"/><ellipse cx="32" cy="39" rx="14" ry="20" fill="#f68b25"/><polygon points="21,36 27,30 28,38" fill="#5a2a00"/><polygon points="43,36 37,30 36,38" fill="#5a2a00"/><path d="M20 45Q32 54 44 45L40 47 37 44 34 48 30 44 27 48 24 44Z" fill="#5a2a00"/>`,
      ),
    },
    {
      id: 'ghost',
      l: 'Ghost',
      g: 'Halloween',
      kind: 'color',
      svg: SV(
        `<path d="M12 58V30A20 20 0 0 1 52 30V58L45 52 39 58 32 52 25 58 19 52Z" fill="#f4f4ff"/><ellipse cx="25" cy="30" rx="4" ry="6" fill="#26202e"/><ellipse cx="39" cy="30" rx="4" ry="6" fill="#26202e"/><ellipse cx="32" cy="43" rx="4" ry="5" fill="#26202e"/>`,
      ),
    },
    {
      id: 'witchhat',
      l: 'Witch hat',
      g: 'Halloween',
      kind: 'color',
      svg: SV(
        `<ellipse cx="32" cy="52" rx="29" ry="7" fill="#4a2a6a"/><path d="M17 52L29 7Q35 2 37 11L47 52Z" fill="#6a3c96"/><rect x="18" y="41" width="29" height="7" fill="#ffb020"/><rect x="28.5" y="40" width="8" height="9" fill="none" stroke="#ffe78d" stroke-width="2"/>`,
      ),
    },
    {
      id: 'bat',
      l: 'Bat',
      g: 'Halloween',
      kind: 'color',
      svg: SV(
        `<path d="M32 26C28 26 26 29 26 31L22 27C18 31 10 29 3 25 8 32 8 38 6 45 12 41 18 42 22 47 24 41 28 39 32 43 36 39 40 41 42 47 46 42 52 41 58 45 56 38 56 32 61 25 54 29 46 31 42 27L38 31C38 29 36 26 32 26Z" fill="#4a3560"/><polygon points="28,27 27,19 31,24" fill="#4a3560"/><polygon points="36,27 37,19 33,24" fill="#4a3560"/><circle cx="29.5" cy="31" r="1.6" fill="#ffe78d"/><circle cx="34.5" cy="31" r="1.6" fill="#ffe78d"/>`,
      ),
    },
    {
      id: 'candycorn',
      l: 'Candy corn',
      g: 'Halloween',
      kind: 'color',
      svg: SV(
        `<path d="M12 54Q32 62 52 54L46 40H18Z" fill="#ffc21a"/><path d="M18 40H46L39 22H25Z" fill="#f68b25"/><path d="M25 22H39L32 5Z" fill="#fff7e0"/>`,
      ),
    },
    {
      id: 'tree',
      l: 'Christmas tree',
      g: 'Christmas',
      kind: 'color',
      svg: SV(
        `<rect x="28" y="54" width="8" height="8" fill="#6b3d1f"/><polygon points="32,7 49,27 40,27 54,42 43,42 58,56 6,56 21,42 10,42 24,27 15,27" fill="#2f8a3a"/><polygon points="32,1 34,6 39,6 35,9 37,14 32,11 27,14 29,9 25,6 30,6" fill="#ffe78d"/><circle cx="26" cy="36" r="2.6" fill="#f70e12"/><circle cx="39" cy="47" r="2.6" fill="#ffe78d"/><circle cx="36" cy="25" r="2.2" fill="#7fd6ff"/><circle cx="22" cy="50" r="2.2" fill="#ff8fb8"/>`,
      ),
    },
    {
      id: 'snowflake',
      l: 'Snowflake',
      g: 'Christmas',
      kind: 'white',
      svg: SV(
        [0, 60, 120, 180, 240, 300]
          .map((r) => `<g transform="rotate(${r} 32 32)">${arm}</g>`)
          .join(''),
      ),
    },
    {
      id: 'santahat',
      l: 'Santa hat',
      g: 'Christmas',
      kind: 'color',
      svg: SV(
        `<path d="M10 50Q18 14 42 11Q57 12 55 31H49Q47 22 41 24Q35 30 50 50Z" fill="#e3121c"/><rect x="5" y="45" width="50" height="13" rx="6.5" fill="#fff"/><circle cx="53" cy="32" r="7" fill="#fff"/>`,
      ),
    },
    {
      id: 'reindeer',
      l: 'Reindeer',
      g: 'Christmas',
      kind: 'color',
      svg: SV(
        `<path d="M25 23L19 6M21 13L12 10M39 23L45 6M43 13L52 10" stroke="#8a5a2b" stroke-width="4" stroke-linecap="round" fill="none"/><ellipse cx="17" cy="29" rx="8" ry="4" fill="#8a5a2b" transform="rotate(-25 17 29)"/><ellipse cx="47" cy="29" rx="8" ry="4" fill="#8a5a2b" transform="rotate(25 47 29)"/><ellipse cx="32" cy="38" rx="14" ry="18" fill="#a86b35"/><ellipse cx="32" cy="50" rx="10" ry="8" fill="#c98f55"/><circle cx="26" cy="34" r="2.6" fill="#1f1a1a"/><circle cx="38" cy="34" r="2.6" fill="#1f1a1a"/><circle cx="32" cy="49" r="5.5" fill="#f70e12"/><circle cx="30.5" cy="47.5" r="1.6" fill="#ffb0b0"/>`,
      ),
    },
    {
      id: 'gift',
      l: 'Gift',
      g: 'Christmas',
      kind: 'color',
      svg: SV(
        `<rect x="8" y="25" width="48" height="34" rx="3" fill="#e3121c"/><rect x="6" y="18" width="52" height="11" rx="2" fill="#fb3133"/><rect x="28" y="18" width="8" height="41" fill="#ffe78d"/><path d="M32 18Q20 3 15 11Q15 18 32 18Q49 18 49 11Q44 3 32 18Z" fill="none" stroke="#ffe78d" stroke-width="4"/>`,
      ),
    },
    {
      id: 'candycane',
      l: 'Candy cane',
      g: 'Christmas',
      kind: 'color',
      svg: SV(
        `<path d="M40 58V21A10 10 0 0 0 20 21" stroke="#fff" stroke-width="10" fill="none" stroke-linecap="round"/><path d="M40 58V21A10 10 0 0 0 20 21" stroke="#e3121c" stroke-width="10" fill="none" stroke-dasharray="5 6"/>`,
      ),
    },
    {
      id: 'maple',
      l: 'Maple leaf (fall colors)',
      g: 'Fall',
      kind: 'white',
      multi: 'fall',
      svg: SV(
        `<path d="M32 3L36 16 44 12 42 24 54 20 50 30 61 34 47 40 49 48 36 44 34 60H30L28 44 15 48 17 40 3 34 14 30 10 20 22 24 20 12 28 16Z" fill="#fff"/><path d="M32 58V22" stroke="rgba(0,0,0,.25)" stroke-width="2"/>`,
      ),
    },
    {
      id: 'leaf',
      l: 'Leaf (fall colors)',
      g: 'Fall',
      kind: 'white',
      multi: 'fall',
      svg: SV(
        `<path d="M32 5C51 14 55 37 34 56L32 61 30 56C9 37 13 14 32 5Z" fill="#fff"/><path d="M32 58V14M32 30L22 22M32 40L42 31M32 47L23 40" stroke="rgba(0,0,0,.25)" stroke-width="2" fill="none"/>`,
      ),
    },
    {
      id: 'acorn',
      l: 'Acorn',
      g: 'Fall',
      kind: 'color',
      svg: SV(
        `<path d="M17 30H47Q47 52 32 61Q17 52 17 30Z" fill="#c98f55"/><path d="M12 30Q12 16 32 16Q52 16 52 30Z" fill="#7a4a22"/><rect x="30" y="6" width="4" height="11" rx="2" fill="#5a3518"/>`,
      ),
    },
    {
      id: 'sunglasses',
      l: 'Sunglasses',
      g: 'Summer',
      kind: 'color',
      svg: SV(
        `<path d="M3 23H61" stroke="#f70e12" stroke-width="4" stroke-linecap="round"/><path d="M7 23H29Q29 42 18 42Q7 42 7 23Z" fill="#2b2b4a" stroke="#f70e12" stroke-width="3"/><path d="M35 23H57Q57 42 46 42Q35 42 35 23Z" fill="#2b2b4a" stroke="#f70e12" stroke-width="3"/><path d="M12 27L18 27M40 27L46 27" stroke="#9fd8ff" stroke-width="3" stroke-linecap="round"/>`,
      ),
    },
    {
      id: 'beachball',
      l: 'Beach ball',
      g: 'Summer',
      kind: 'color',
      svg: SV(
        `<circle cx="32" cy="32" r="28" fill="#fff"/><path d="M32 4A28 28 0 0 1 59 25L32 32Z" fill="#f70e12"/><path d="M57 44A28 28 0 0 1 29 60L32 32Z" fill="#2a8cff"/><path d="M8 47A28 28 0 0 1 9 17L32 32Z" fill="#ffc21a"/><circle cx="32" cy="32" r="4" fill="#fff"/><circle cx="32" cy="32" r="28" fill="none" stroke="rgba(0,0,0,.15)" stroke-width="2"/>`,
      ),
    },
    {
      id: 'surfboard',
      l: 'Surfboard',
      g: 'Summer',
      kind: 'color',
      svg: SV(
        `<g transform="rotate(35 32 32)"><ellipse cx="32" cy="32" rx="11" ry="30" fill="#ffe78d"/><rect x="30" y="3" width="4" height="58" fill="#f70e12"/><path d="M22 42H42" stroke="#2a8cff" stroke-width="3"/></g>`,
      ),
    },
    {
      id: 'palm',
      l: 'Palm tree',
      g: 'Summer',
      kind: 'color',
      svg: SV(
        `<path d="M30 61Q27 42 34 22" stroke="#8a5a2b" stroke-width="6" fill="none" stroke-linecap="round"/><path d="M34 22Q20 9 5 18Q20 15 34 22Z M34 22Q48 7 60 16Q46 15 34 22Z M34 22Q21 26 13 39Q26 28 34 22Z M34 22Q48 26 55 39Q44 28 34 22Z M34 22Q35 6 26 2Q39 8 34 22Z" fill="#2f9a45"/><circle cx="31" cy="25" r="3" fill="#6b3d1f"/><circle cx="37" cy="25" r="3" fill="#6b3d1f"/>`,
      ),
    },
    {
      id: 'sun',
      l: 'Sun',
      g: 'Summer',
      kind: 'color',
      svg: SV(
        [0, 45, 90, 135, 180, 225, 270, 315]
          .map(
            (r) =>
              `<path d="M32 3V12" stroke="#ffb020" stroke-width="5" stroke-linecap="round" transform="rotate(${r} 32 32)"/>`,
          )
          .join('') + `<circle cx="32" cy="32" r="16" fill="#ffd23f"/>`,
      ),
    },
    {
      id: 'bunny',
      l: 'Bunny',
      g: 'Easter',
      kind: 'color',
      svg: SV(
        `<ellipse cx="23" cy="17" rx="6.5" ry="15" fill="#f4eeee"/><ellipse cx="41" cy="17" rx="6.5" ry="15" fill="#f4eeee"/><ellipse cx="23" cy="18" rx="3" ry="10" fill="#ffb6c8"/><ellipse cx="41" cy="18" rx="3" ry="10" fill="#ffb6c8"/><circle cx="32" cy="43" r="18" fill="#f4eeee"/><circle cx="25" cy="40" r="2.5" fill="#2a2226"/><circle cx="39" cy="40" r="2.5" fill="#2a2226"/><ellipse cx="32" cy="47" rx="3" ry="2.2" fill="#ff8fb8"/><circle cx="22" cy="48" r="3" fill="#ffd0dc"/><circle cx="42" cy="48" r="3" fill="#ffd0dc"/>`,
      ),
    },
    {
      id: 'egg',
      l: 'Easter egg',
      g: 'Easter',
      kind: 'color',
      svg: SV(
        `<ellipse cx="32" cy="34" rx="20" ry="27" fill="#7fd6ff"/><path d="M12 34L18 28 24 34 30 28 36 34 42 28 48 34 52 30" stroke="#fff" stroke-width="3" fill="none"/><circle cx="25" cy="20" r="3" fill="#ff8fb8"/><circle cx="39" cy="20" r="3" fill="#ffe78d"/><circle cx="25" cy="47" r="3" fill="#ffe78d"/><circle cx="39" cy="47" r="3" fill="#ff8fb8"/>`,
      ),
    },
    {
      id: 'flower',
      l: 'Flower',
      g: 'Easter',
      kind: 'color',
      svg: SV(
        [0, 72, 144, 216, 288]
          .map(
            (r) =>
              `<ellipse cx="32" cy="16" rx="9" ry="13" fill="#ff8fb8" transform="rotate(${r} 32 32)"/>`,
          )
          .join('') + `<circle cx="32" cy="32" r="8" fill="#ffd23f"/>`,
      ),
    },
    {
      id: 'football',
      l: 'Football',
      g: 'Sports',
      kind: 'color',
      svg: SV(
        `<g transform="rotate(-35 32 32)"><ellipse cx="32" cy="32" rx="29" ry="17" fill="#8a4a22"/><path d="M8 32Q32 22 56 32" stroke="none"/><path d="M20 32H44" stroke="#fff" stroke-width="2.5"/><path d="M24 28V36M28 28V36M32 28V36M36 28V36M40 28V36" stroke="#fff" stroke-width="2"/><path d="M8 25Q11 32 8 39M56 25Q53 32 56 39" stroke="#fff" stroke-width="2" fill="none"/></g>`,
      ),
    },
    {
      id: 'baseball',
      l: 'Baseball',
      g: 'Sports',
      kind: 'color',
      svg: SV(
        `<circle cx="32" cy="32" r="28" fill="#f7f4ee"/><path d="M15 10Q27 32 15 54M49 10Q37 32 49 54" stroke="#e3121c" stroke-width="2.5" fill="none" stroke-dasharray="3 3"/>`,
      ),
    },
    {
      id: 'soccer',
      l: 'Soccer ball',
      g: 'Sports',
      kind: 'color',
      svg: SV(
        `<circle cx="32" cy="32" r="28" fill="#fff"/><polygon points="32,22 41,29 38,40 26,40 23,29" fill="#1f1f24"/><path d="M32 22V10M41 29L52 24M38 40L45 50M26 40L19 50M23 29L12 24" stroke="#1f1f24" stroke-width="2"/><path d="M26 6L32 10 38 6M55 18L52 24 57 31M51 52L45 50 41 57M23 57L19 50 13 52M7 31L12 24 9 18" fill="#1f1f24" stroke="#1f1f24" stroke-width="2"/><circle cx="32" cy="32" r="28" fill="none" stroke="rgba(0,0,0,.2)" stroke-width="2"/>`,
      ),
    },
    {
      id: 'basketball',
      l: 'Basketball',
      g: 'Sports',
      kind: 'color',
      svg: SV(
        `<circle cx="32" cy="32" r="28" fill="#ee7a22"/><path d="M32 4V60M4 32H60M12 12Q26 32 12 52M52 12Q38 32 52 52" stroke="#3a1f10" stroke-width="2.5" fill="none"/>`,
      ),
    },
    {
      id: 'tennis',
      l: 'Tennis ball',
      g: 'Sports',
      kind: 'color',
      svg: SV(
        `<circle cx="32" cy="32" r="28" fill="#cfe63a"/><path d="M10 15Q28 32 10 49M54 15Q36 32 54 49" stroke="#fff" stroke-width="3.5" fill="none"/>`,
      ),
    },
    {
      id: 'puck',
      l: 'Hockey puck',
      g: 'Sports',
      kind: 'color',
      svg: SV(
        `<ellipse cx="32" cy="40" rx="26" ry="10" fill="#111"/><rect x="6" y="28" width="52" height="12" fill="#111"/><ellipse cx="32" cy="28" rx="26" ry="10" fill="#2a2a30"/><ellipse cx="32" cy="28" rx="16" ry="5" fill="none" stroke="#555" stroke-width="1.5"/>`,
      ),
    },
    {
      id: 'balloon',
      l: 'Balloon',
      g: 'Party',
      kind: 'color',
      svg: SV(
        `<path d="M32 46Q30 54 34 62" stroke="#ddd" stroke-width="1.5" fill="none"/><ellipse cx="32" cy="24" rx="17" ry="21" fill="#f70e12"/><polygon points="29,45 35,45 32,49" fill="#c80d16"/><ellipse cx="25" cy="15" rx="4" ry="6" fill="#ff8a8a"/>`,
      ),
    },
    {
      id: 'confetti',
      l: 'Confetti (5 colors)',
      g: 'Party',
      kind: 'white',
      multi: 'sprinkle',
      svg: SV(`<rect x="22" y="14" width="20" height="36" rx="2" fill="#fff"/>`),
    },
  ];
  const SHAPE_BY = {};
  SHAPE_DEFS.forEach((d) => (SHAPE_BY[d.id] = d));
  const FALL_PAL = [
    [217, 84, 30],
    [232, 163, 61],
    [184, 50, 31],
    [242, 193, 78],
    [150, 90, 40],
  ];
  const CUSTOM = {};
  /* Register an uploaded SVG shape. id must start with "u:". */
  function registerShape(id, s) {
    if (!id || !s || typeof s.svg !== 'string') return;
    CUSTOM[id] = { id, l: s.name || 'Custom shape', kind: 'color', svg: s.svg, custom: true };
    delete SHP[id];
    for (const k of TINT.keys()) if (k.startsWith(id + '|')) TINT.delete(k);
  }
  function shapeInfo(id) {
    if (!id) return null;
    if (SHAPE_BY[id]) return SHAPE_BY[id];
    if (CUSTOM[id]) return CUSTOM[id];
    if (id.startsWith('e:'))
      return { id, l: id.slice(2) + ' emoji', kind: 'color', emoji: id.slice(2) };
    return null;
  }
  const SHP = {};
  function shapeBase(id) {
    if (SHP[id]) return SHP[id];
    const d = shapeInfo(id);
    if (!d || d.kind === 'special') return null;
    let c;
    if (d.emoji) {
      c = document.createElement('canvas');
      c.width = c.height = 96;
      const g = c.getContext('2d');
      g.font = '78px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(d.emoji, 48, 52);
    } else if (d.svg) c = renderSVG(d.svg, 96).canvas;
    if (c) SHP[id] = c;
    return c || null;
  }
  const TINT = new Map();
  function shapeTinted(id, c, mode) {
    const base = shapeBase(id);
    if (!base) return null;
    const key = id + '|' + mode + '|' + (((c[0] >> 3) << 10) | ((c[1] >> 3) << 5) | (c[2] >> 3));
    let t = TINT.get(key);
    if (t) return t;
    if (TINT.size > 2500) TINT.clear();
    t = document.createElement('canvas');
    t.width = t.height = base.width;
    const g = t.getContext('2d');
    g.drawImage(base, 0, 0);
    g.globalCompositeOperation = mode === 'in' ? 'source-in' : 'source-atop';
    g.globalAlpha = mode === 'in' ? 1 : 0.78;
    g.fillStyle = rgba(c, 1);
    g.fillRect(0, 0, t.width, t.height);
    TINT.set(key, t);
    return t;
  }
  function drawShape(ctx, p, sz, rot, c, al) {
    if (al <= 0.004) return;
    const id = p.id;
    if (id === 'spark') {
      dsp(ctx, p.x, p.y, p.gs || sz * 2.5, c, al * 0.6);
      ctx.strokeStyle = rgba(c, al);
      ctx.lineWidth = Math.max(0.8, (p.core || sz * 0.35) * 2);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x - p.vx * 0.02, p.y - p.vy * 0.02);
      ctx.stroke();
      return;
    }
    if (id === 'puffs') {
      dsp(ctx, p.x, p.y, sz * 2.2, c, al * 0.9);
      return;
    }
    const d = shapeInfo(id);
    if (!d) return;
    let cv,
      gc = c;
    if (d.kind === 'white') {
      let col = c;
      if (d.multi && p.own) {
        col = d.multi === 'fall' ? FALL_PAL[p.ci % 5] : (p.pal && p.pal[p.ci % 5]) || c;
      }
      gc = col;
      cv = shapeTinted(id, col, 'in');
    } else cv = p.own ? shapeBase(id) : shapeTinted(id, c, 'atop');
    if (!cv) return;
    if (p.glow > 0) dsp(ctx, p.x, p.y, sz * 2.4 * p.glow, gc, al * 0.4);
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = Math.min(1, al);
    ctx.translate(p.x, p.y);
    ctx.rotate(rot);
    const s = sz * 1.25;
    ctx.drawImage(cv, -s, -s, 2 * s, 2 * s);
    ctx.restore();
  }
  const SLOTSETS = {
    tail: {
      s: ['tailShape', 'tailShape2', 'tailShape3'],
      m: ['tailMix1', 'tailMix2', 'tailMix3'],
      z: ['tailScale1', 'tailScale2', 'tailScale3'],
      o: ['tailOwn1', 'tailOwn2', 'tailOwn3'],
    },
    ts: {
      s: ['tsShape1', 'tsShape2', 'tsShape3'],
      m: ['tsMix1', 'tsMix2', 'tsMix3'],
      z: ['tsScale1', 'tsScale2', 'tsScale3'],
      o: ['tsOwn1', 'tsOwn2', 'tsOwn3'],
    },
    burst: {
      s: ['bShape1', 'bShape2', 'bShape3'],
      m: ['bMix1', 'bMix2', 'bMix3'],
      z: ['bScale1', 'bScale2', 'bScale3'],
      o: ['bOwn1', 'bOwn2', 'bOwn3'],
    },
  };
  function pickSlot(v, set) {
    const S = SLOTSETS[set];
    let tot = 0;
    const c = [];
    for (let i = 0; i < 3; i++) {
      const id = v[S.s[i]],
        w = v[S.m[i]];
      if (id && w > 0 && (id === 'spark' || id === 'puffs' || shapeInfo(id))) {
        c.push([i, w]);
        tot += w;
      }
    }
    if (!c.length) return null;
    let r = Math.random() * tot,
      pick = c[c.length - 1][0];
    for (const [i, w] of c) {
      if ((r -= w) <= 0) {
        pick = i;
        break;
      }
    }
    return { id: v[S.s[pick]], own: !!v[S.o[pick]], sc: v[S.z[pick]] };
  }
  /* Shapes a boost's settings actually use (for packaging and the emoji check). */
  function shapesUsed(v) {
    const out = new Set();
    const add = (set, on) => {
      if (!on) return;
      const S = SLOTSETS[set];
      for (let i = 0; i < 3; i++) {
        const id = v[S.s[i]];
        if (id && v[S.m[i]] > 0) out.add(id);
      }
    };
    add('burst', v.burstCount > 0);
    add('tail', v.tailOn && v.tailRate > 0);
    add('ts', v.tailOn && v.tailSparks > 0);
    return [...out];
  }

  /* ===================== particles ===================== */
  function spawnSpark(L, S, x, y, nx, ny) {
    const list = L.parts;
    if (list.length > 1500) return;
    const ang = Math.atan2(ny, nx) + ((Math.random() - 0.5) * S.sparkSpread * Math.PI) / 180;
    const sp = S.sparkSpeed * (1 - S.sparkSpeedVar * Math.random());
    const life = Math.max(0.04, S.sparkLife * (1 + S.sparkLifeVar * (Math.random() * 2 - 1) * 0.5));
    list.push({
      k: 'spark',
      x,
      y,
      vx: Math.cos(ang) * sp,
      vy: Math.sin(ang) * sp,
      life,
      max: life,
      g: S.sparkGravity,
      w: S.sparkWind,
      drag: S.sparkDrag,
      c0: S.sparkStart,
      c1: S.sparkEnd,
      gc: S.sparkGlowColor,
      core: S.sparkCoreSize,
      gs: S.sparkGlowSize,
      go: S.sparkGlowOpacity,
      b: lerp(S.sparkMinBright, Math.max(S.sparkMinBright, S.sparkMaxBright), Math.random()),
      st: S.sparkStreak,
    });
  }
  function stepParts(list, rdt0, S) {
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i];
      const rdt =
        rdt0 *
        (S && !p.fixed
          ? p.k === 'spark'
            ? S.sparkAnim
            : p.k === 'amber'
              ? S.amberAnim
              : p.k === 'fire'
                ? S.fireAnim
                : 1
          : 1);
      p.life -= rdt;
      if (p.life <= 0) {
        list[i] = list[list.length - 1];
        list.pop();
        continue;
      }
      if (p.turb) p.vx += (Math.random() - 0.5) * p.turb * rdt * 8;
      const d = Math.pow(1 - clamp(p.drag || 0, 0, 0.99), rdt * 60);
      p.vx *= d;
      p.vy *= d;
      p.vx += (p.w || 0) * rdt;
      p.vy += (p.g || 0) * rdt;
      p.x += p.vx * rdt;
      p.y += p.vy * rdt;
      if (p.rise) p.y -= p.rise * rdt0;
    }
  }
  function drawParts(ctx, list, fr, m) {
    for (const p of list) {
      const lt = p.life / p.max,
        fade = fr ? edgeFade(fr, m, p.x, p.y) : 1;
      if (fade <= 0.01) continue;
      if (p.k === 'spark' || p.k === 'burst') {
        const c = mix(p.c1, p.c0, lt),
          al = p.b * Math.sqrt(lt) * fade;
        dsp(ctx, p.x, p.y, p.gs, p.gc || c, al * (p.go ?? 0.6));
        if (p.st > 0) {
          ctx.strokeStyle = rgba(c, al);
          ctx.lineWidth = p.core * 2;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * p.st, p.y - p.vy * p.st);
          ctx.stroke();
        } else {
          ctx.fillStyle = rgba(c, al);
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.core, 0, TAU);
          ctx.fill();
        }
      } else if (p.k === 'amber') {
        const c = mix(p.c1, p.c0, lt),
          al = p.op * lt * fade;
        dsp(ctx, p.x, p.y, p.gs, c, al * 0.7);
        ctx.fillStyle = rgba(c, al);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (0.4 + 0.6 * lt), 0, TAU);
        ctx.fill();
      } else if (p.k === 'fire') {
        const t = 1 - lt,
          c = t < 0.4 ? mix(p.cc, p.cm, t / 0.4) : mix(p.cm, p.ct, (t - 0.4) / 0.6);
        dsp(
          ctx,
          p.x,
          p.y,
          p.size * (1 - 0.55 * t) * 1.8,
          c,
          p.op * Math.pow(lt, 0.7) * 0.75 * fade,
        );
      } else if (p.k === 'shape') {
        const c = mix(p.c1, p.c0, lt),
          al = clamp(p.op * Math.pow(lt, 0.7), 0, 1) * fade,
          sz = Math.max(0.2, p.size * (1 + (p.grow || 0) * (1 - lt))),
          rot = p.rot + p.spin * (p.max - p.life);
        drawShape(ctx, p, sz, rot, c, al);
      } else if (p.k === 'ember') {
        dsp(ctx, p.x, p.y, p.size * 2.5, p.c, lt * 0.8 * fade);
      }
    }
  }

  /* ===================== lightning, orbit, comets ===================== */
  function jagLine(pts, x0, y0, dx, dy, len, n, jag, seed) {
    const px = -dy,
      py = dx,
      out = [[x0, y0]];
    for (let i = 1; i <= n; i++) {
      const t = i / n,
        off = i === n ? 0 : (hh(seed + i * 3.7) * 2 - 1) * jag;
      out.push([x0 + dx * len * t + px * off, y0 + dy * len * t + py * off]);
    }
    pts.push(out);
    return out;
  }
  function buildBolt(R, L, b, n, S, t, a) {
    const span = clamp(S.boltCoverage, 0.02, 1) * L,
      start = (b / n + a.clk.boltPos) * L + (a.phase * L) / TAU;
    const N = Math.max(2, Math.ceil(span / Math.max(3, S.boltSegment)));
    const seed = Math.floor(t * S.boltRefresh) * 7.13 + b * 31.7 + a.id * 101.3,
      full = S.boltCoverage >= 0.98;
    const main = [];
    for (let i = 0; i <= N; i++) {
      const s = start + (span * i) / N,
        p = rrPoint(R, s);
      const env = full ? 1 : Math.pow(Math.sin((Math.PI * i) / N), 0.6);
      const jag = (hh(seed + i * 1.618) * 2 - 1) * S.boltJag;
      const wave =
        S.boltWaveAmp *
        Math.sin(
          (TAU * ((span * i) / N)) / Math.max(1, S.boltWaveLen) - t * S.boltWaveSpeed * TAU + b,
        );
      const d = (jag + wave) * env;
      main.push([p.x + p.nx * d, p.y + p.ny * d, p.nx, p.ny]);
    }
    if (full) main[N] = main[0];
    const branches = [],
      bc = S.boltBranches,
      nb = Math.floor(bc) + (hh(seed + 77) < bc % 1 ? 1 : 0);
    for (let k = 0; k < nb; k++) {
      const idx = 1 + Math.floor(hh(seed + 50 + k * 5.3) * (N - 1)),
        bp = main[Math.min(idx, N)];
      const side = hh(seed + 61 + k) < 0.5 ? -1 : 1,
        ang =
          Math.atan2(bp[3], bp[2]) +
          ((side * S.branchAngle * Math.PI) / 180) * (0.5 + 0.5 * hh(seed + 62 + k));
      const len = S.branchLength * (0.6 + 0.7 * hh(seed + 63 + k)),
        dx = Math.cos(ang),
        dy = Math.sin(ang);
      const br = jagLine(
        branches,
        bp[0],
        bp[1],
        dx,
        dy,
        len,
        4,
        S.boltJag * 0.5 + 1,
        seed + 70 + k * 9,
      );
      if (hh(seed + 90 + k) < S.branchFork) {
        const m = br[2],
          a2 = ang - side * 0.6;
        jagLine(
          branches,
          m[0],
          m[1],
          Math.cos(a2),
          Math.sin(a2),
          len * 0.55,
          3,
          S.boltJag * 0.4 + 1,
          seed + 95 + k,
        );
      }
    }
    return {
      main,
      branches,
      bright: lerp(S.boltMinBright, Math.max(S.boltMinBright, S.boltMaxBright), hh(seed + 99)),
    };
  }
  function strokePoly(ctx, pts) {
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
    ctx.stroke();
  }
  function drawBolts(ctx, a, S, t) {
    const R = cardRR(a.rect, S.boltOffset),
      L = rrLen(R),
      cnt = Math.max(0, S.boltCount),
      n = Math.ceil(cnt);
    a.boltPts = [];
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (let b = 0; b < n; b++) {
      const w = b < Math.floor(cnt) ? 1 : cnt - Math.floor(cnt);
      if (w <= 0.01) continue;
      const bolt = buildBolt(R, L, b, n, S, t, a),
        al = clamp(a.amp * bolt.bright * w, 0, 1);
      const paths = [bolt.main, ...bolt.branches];
      if (S.boltGlowWidth > 0) {
        ctx.save();
        ctx.strokeStyle = rgba(S.boltGlowColor, al * 0.55);
        ctx.lineWidth = S.boltGlowWidth;
        ctx.shadowColor = rgba(S.boltGlowColor, al);
        ctx.shadowBlur = S.boltGlowBlur * DPR;
        paths.forEach((p) => strokePoly(ctx, p));
        ctx.restore();
      }
      ctx.strokeStyle = rgba(S.boltCoreColor, al);
      ctx.lineWidth = S.boltCoreWidth;
      strokePoly(ctx, bolt.main);
      ctx.lineWidth = S.boltCoreWidth * 0.7;
      bolt.branches.forEach((p) => strokePoly(ctx, p));
      for (const p of bolt.main) a.boltPts.push(p);
    }
  }
  function drawOrbit(ctx, a, S) {
    const R = cardRR(a.rect, S.orbitOffset),
      cx = R.x + R.w / 2,
      cy = R.y + R.h / 2,
      n = Math.max(1, Math.round(S.orbitComets)),
      tail = clamp(S.orbitTail, 0.05, 0.95);
    let style;
    if (ctx.createConicGradient) {
      const g = ctx.createConicGradient(a.clk.orbit * TAU + a.phase, cx, cy),
        seg = 1 / n;
      for (let i = 0; i < n; i++) {
        const b = i * seg;
        g.addColorStop(b, rgba(S.orbitTailColor, 0));
        g.addColorStop(b + seg * (1 - tail), rgba(S.orbitTailColor, 0));
        g.addColorStop(b + seg * 0.95, rgba(S.orbitTailColor, 0.85));
        g.addColorStop(b + seg * 0.99, rgba(S.orbitHead, 1));
        if (i < n - 1) g.addColorStop(b + seg * 0.999, rgba(S.orbitHead, 1));
      }
      g.addColorStop(1, rgba(S.orbitHead, 0));
      style = g;
    } else style = rgba(S.orbitHead, 0.6);
    ctx.save();
    ctx.globalAlpha = clamp(S.orbitOpacity * a.amp, 0, 1);
    ctx.strokeStyle = style;
    ctx.lineWidth = S.orbitWidth;
    ctx.shadowColor = rgba(S.orbitHead, 0.9);
    ctx.shadowBlur = S.orbitGlow * DPR;
    rr(ctx, R.x, R.y, R.w, R.h, R.r);
    ctx.stroke();
    ctx.restore();
  }
  function drawComets(ctx, a, S) {
    const R = cardRR(a.rect, S.cometOffset),
      L = rrLen(R),
      n = Math.round(S.cometCount),
      op = clamp(S.cometOpacity * a.amp, 0, 1),
      seg = 18;
    ctx.lineCap = 'round';
    for (let i = 0; i < n; i++) {
      const s0 = (i / n + a.clk.comet) * L + a.phase * 20,
        dir = S.cometSpeed >= 0 ? 1 : -1;
      let prev = null;
      for (let k = 0; k <= seg; k++) {
        const s = s0 - (dir * S.cometTail * L * k) / seg,
          p = rrPoint(R, s),
          wob = S.cometWobble * Math.sin((TAU * S.cometWobbleFreq * s) / L + a.clk.cosmic * 3);
        const x = p.x + p.nx * wob,
          y = p.y + p.ny * wob,
          f = k / seg;
        if (prev) {
          ctx.strokeStyle = rgba(
            mix(S.cometHead, S.cometTailColor, Math.min(1, f * 1.6)),
            op * (1 - f),
          );
          ctx.lineWidth = S.cometHeadSize * 1.5 * (1 - f) + 0.3;
          ctx.beginPath();
          ctx.moveTo(prev[0], prev[1]);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        if (k === 0) {
          dsp(ctx, x, y, S.cometHeadSize * 4, S.cometHead, op);
          dsp(ctx, x, y, S.cometHeadSize * 8, S.cometTailColor, op * 0.35);
        }
        prev = [x, y];
      }
    }
  }

  /* ===================== one look layer (build or afterglow) ===================== */
  function mkRunner(card, off) {
    return {
      id: card.uid + off * 13,
      phase: card.phase + off,
      stars: card.stars,
      rect: null,
      amp: 0,
      vis: 0,
      visD: 0,
      D: 0,
      S: null,
      clk: { bolt: 0, boltPos: 0, orbit: 0, cosmic: 0, comet: 0 },
      parts: [],
      backParts: [],
      pulses: [],
      boltPts: [],
      acc: {},
    };
  }
  function runEmitters(L, rdt, crr, cL) {
    const S = L.S,
      A = L.acc,
      on = L.vis > 0.01;
    const emit = (key, rate, fn) => {
      A[key] = (A[key] || 0) + rate * rdt;
      let guard = 0;
      while (A[key] >= 1 && guard++ < 200) {
        A[key]--;
        fn();
      }
    };
    if (on && S.sparksOn && S.sparkRate > 0)
      emit('spark', S.sparkRate * L.vis, () => {
        const canBolt = S.boltOn && L.boltPts.length > 0;
        const useBolt =
          canBolt &&
          (S.sparkSource === 'bolts' || (S.sparkSource === 'both' && Math.random() < 0.6));
        if (S.sparkSource === 'bolts' && !useBolt) return;
        if (useBolt) {
          const p = L.boltPts[(Math.random() * L.boltPts.length) | 0];
          spawnSpark(L, S, p[0], p[1], p[2], p[3]);
        } else {
          const p = rrPoint(crr, Math.random() * cL);
          spawnSpark(L, S, p.x, p.y, p.nx, p.ny);
        }
      });
    if (on && S.amberOn && S.amberRate > 0)
      emit('amber', S.amberRate * L.vis, () => {
        if (L.parts.length > 1500) return;
        let p = rrPoint(crr, Math.random() * cL);
        if (p.ny > 0.5 && Math.random() < S.amberRise) p = rrPoint(crr, Math.random() * cL);
        const dx = p.nx * (1 - S.amberRise),
          dy = p.ny * (1 - S.amberRise) - S.amberRise;
        const ang = Math.atan2(dy, dx) + (Math.random() - 0.5) * S.amberSpread * Math.PI,
          sp = S.amberSpeed * (0.6 + Math.random() * 0.8),
          life = S.amberLife * (0.6 + Math.random() * 0.6);
        L.parts.push({
          k: 'amber',
          x: p.x,
          y: p.y,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          life,
          max: life,
          g: -10 * S.amberRise,
          w: S.amberWind,
          drag: 0.015,
          size: S.amberSize * (0.6 + Math.random() * 0.7),
          gs: S.amberGlow,
          c0: S.amberStart,
          c1: S.amberEnd,
          op: S.amberOpacity * L.vis,
        });
      });
    if (on && S.fireOn && S.fireRate > 0)
      emit('fire', S.fireRate * L.vis, () => {
        const list = S.fireLayer === 'front' ? L.parts : L.backParts;
        if (list.length > 1500) return;
        let p,
          tries = 0;
        do {
          p = rrPoint(crr, Math.random() * cL);
          tries++;
        } while (
          tries < 8 &&
          ((S.fireSides === 'top' && p.ny > -0.3) || (S.fireSides === 'topSides' && p.ny > 0.3))
        );
        const life = 0.35 + Math.random() * 0.3,
          sp = S.fireHeight / life;
        list.push({
          k: 'fire',
          x: p.x,
          y: p.y,
          vx: p.nx * sp * 0.55 + (Math.random() - 0.5) * S.fireTurbulence,
          vy: p.ny * sp * 0.55 - sp * 0.55,
          life,
          max: life,
          g: -20,
          w: S.fireWind,
          turb: S.fireTurbulence,
          drag: 0.02,
          size: S.fireSize * (0.7 + Math.random() * 0.6),
          cc: S.fireCore,
          cm: S.fireMid,
          ct: S.fireTip,
          op: S.fireOpacity * L.vis,
        });
      });
    if (on && S.glowOn && S.pulseRingRate > 0)
      emit('pulse', S.pulseRingRate, () =>
        L.pulses.push({
          age: 0,
          size: S.pulseRingSize,
          w: S.pulseRingWidth,
          c: S.pulseRingColor,
          op: S.pulseRingOpacity * L.amp,
        }),
      );
  }
  function stepRunner(L, rdt) {
    const S = L.S;
    stepParts(L.parts, rdt, S);
    stepParts(L.backParts, rdt, S);
    const C = L.clk;
    C.bolt += rdt * S.boltAnim;
    C.boltPos += (rdt * S.boltAnim * S.boltDrift) / 100;
    C.orbit += rdt * S.orbitAnim * S.orbitSpeed;
    C.cosmic += rdt * S.cosmicAnim;
    C.comet += rdt * S.cosmicAnim * S.cometSpeed;
    for (const pu of L.pulses) pu.age += rdt * S.pulseAnim;
  }
  function ampOf(L, t, extra) {
    const S = L.S,
      ph = L.phase,
      fs = S.flickerSpeed;
    const noise =
      Math.sin(t * fs + ph) * 0.5 +
      Math.sin(t * fs * 2.31 + ph * 2) * 0.3 +
      Math.sin(t * fs * 5.13 + ph * 3) * 0.2;
    const flick = 1 - S.flicker * 0.55 * (0.5 + 0.5 * noise),
      pulse = 1 + S.pulseDepth * 0.5 * Math.sin(TAU * S.pulseHz * t + ph);
    return L.vis > 0.001 ? S.masterOpacity * flick * pulse * L.vis * extra : 0;
  }
  function drawBack(ctx, L, r, M) {
    const S = L.S,
      on = L.amp > 0.002;
    if (!S) return;
    if (on && S.glowOn) {
      glow(ctx, r, S.glowOuter, S.glowOpacity * L.amp, S.glowSize);
      glow(ctx, r, S.glowInner, S.glowOpacity * L.amp * 0.9, S.glowInnerSize);
    }
    for (let i = L.pulses.length - 1; i >= 0; i--) {
      const pu = L.pulses[i],
        p = pu.age / 1.1;
      if (p >= 1) {
        L.pulses.splice(i, 1);
        continue;
      }
      const e = easeOut(p),
        R2 = cardRR(r, e * pu.size);
      ctx.strokeStyle = rgba(pu.c, pu.op * (1 - p));
      ctx.lineWidth = pu.w * (1 - p * 0.5);
      rr(ctx, R2.x, R2.y, R2.w, R2.h, R2.r);
      ctx.stroke();
    }
    if (on && S.cosmicOn) {
      const crr = cardRR(r),
        cL = rrLen(crr),
        n = Math.min(L.stars.length, Math.round(S.starCount));
      for (let j = 0; j < n; j++) {
        const st = L.stars[j],
          p = rrPoint(crr, st.u * cL),
          d = st.d * M,
          x = p.x + p.nx * d,
          y = p.y + p.ny * d,
          tw = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(L.clk.cosmic * TAU * S.starTwinkle + st.ph));
        const al = L.amp * tw * S.cometOpacity * edgeFade(r, M, x, y);
        dsp(ctx, x, y, S.starSize * 3.5, S.starColor, al * 0.7);
        ctx.fillStyle = rgba(S.starColor, al);
        ctx.fillRect(x - S.starSize / 2, y - S.starSize / 2, S.starSize, S.starSize);
      }
    }
    drawParts(ctx, L.backParts, r, M);
  }
  function drawFront(ctx, L, r, M) {
    const S = L.S,
      on = L.amp > 0.002;
    if (!S) return;
    if (on && S.orbitOn) drawOrbit(ctx, L, S);
    if (on && S.boltOn) drawBolts(ctx, L, S, L.clk.bolt);
    else L.boltPts = [];
    if (on && S.cosmicOn) drawComets(ctx, L, S);
    drawParts(ctx, L.parts, r, M);
  }

  /* ===================== +1 cherry ===================== */
  const CHERRY = {};
  function cherrySprite(R) {
    const v = R.v,
      key = v.upCherryColor + v.upStemColor + v.upShine;
    if (CHERRY[key]) return CHERRY[key];
    for (const k in CHERRY) delete CHERRY[k];
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path d="M31 24Q30 11 45 3" stroke="${v.upStemColor}" stroke-width="3.5" fill="none" stroke-linecap="round"/><path d="M31 22C23 16 6 19 6 38 6 53 18 61 31 61 44 61 56 53 56 38 56 19 39 16 31 22Z" fill="${v.upCherryColor}"/><ellipse cx="17" cy="33" rx="3.6" ry="7" fill="#ffffff" opacity="${(v.upShine * 0.55).toFixed(2)}" transform="rotate(25 17 33)"/></svg>`;
    return (CHERRY[key] = renderSVG(svg, 128).canvas);
  }
  function drawPlusOne(ctx, R, r, p, label) {
    const v = R.v,
      S = v.upSize,
      lay = label ? 'text' : v.upLayout,
      e = easeOut(p),
      word = label || '+1';
    const pop =
        p < 0.12
          ? lerp(0.5, 1 + v.upPop, p / 0.12)
          : p < 0.28
            ? lerp(1 + v.upPop, 1, (p - 0.12) / 0.16)
            : 1,
      k = S * pop;
    const al = clamp(
      p < 0.06 ? p / 0.06 : p < v.upHold ? 1 : 1 - (p - v.upHold) / Math.max(0.05, 1 - v.upHold),
      0,
      1,
    );
    if (al <= 0.003) return;
    const cx = r.x + r.w / 2,
      cy = r.y + r.h * v.upPosY - v.upRise * e,
      fs = (label ? 13 : 16) * k,
      ch = 18 * k * v.upCherrySize,
      gap = 3 * k;
    ctx.save();
    ctx.font = `700 ${fs.toFixed(1)}px Outfit, system-ui, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    const tw = lay === 'cherry' ? 0 : ctx.measureText(word).width,
      hasC = lay !== 'text';
    let tx,
      ty = cy,
      ccx,
      ccy = cy;
    if (lay === 'above') {
      tx = cx - tw / 2;
      ty = cy + ch * 0.45;
      ccx = cx;
      ccy = cy - ch * 0.55;
    } else if (!hasC) {
      tx = cx - tw / 2;
    } else if (lay === 'cherry') {
      ccx = cx;
    } else {
      const tot = ch + gap + tw;
      if (lay === 'right') {
        tx = cx - tot / 2;
        ccx = tx + tw + gap + ch / 2;
      } else {
        ccx = cx - tot / 2 + ch / 2;
        tx = ccx + ch / 2 + gap;
      }
    }
    if (v.upGlow > 0) {
      ctx.globalCompositeOperation = 'lighter';
      dsp(ctx, cx, cy, Math.max(ch, tw) * 1.3, R.col.upGlowColor, al * v.upGlow);
      ctx.globalCompositeOperation = 'source-over';
    }
    ctx.shadowColor = `rgba(0,0,0,${(0.7 * v.upShadow).toFixed(3)})`;
    ctx.shadowBlur = v.upShadow > 0 ? 4 * DPR : 0;
    ctx.shadowOffsetY = v.upShadow > 0 ? 1 : 0;
    ctx.globalAlpha = al;
    if (hasC) {
      const sp = cherrySprite(R);
      ctx.save();
      ctx.translate(ccx, ccy);
      ctx.rotate(((v.upWobble * Math.PI) / 180) * Math.sin(p * TAU * 1.6) * (1 - p));
      ctx.drawImage(sp, -ch / 2, -ch / 2, ch, ch);
      ctx.restore();
    }
    if (lay !== 'cherry') {
      ctx.fillStyle = rgba(R.col.upTextColor, 1);
      ctx.fillText(word, tx, ty);
    }
    ctx.restore();
  }

  /* ===================== energy helpers (use them with an effect rules package) ===================== */
  /* Pure functions over song objects {E, heat, lastVote, armed, locked}. Times are in seconds. */
  const energy = {
    lightThreshold(R) {
      R = R.v || R;
      let e = 0;
      for (let i = 0; i < Math.round(R.lightUpVotes); i++)
        e = Math.min(1, e + R.votePower * Math.pow(1 - e, R.resistance));
      return e * 0.999;
    },
    applyVote(songs, song, R, now) {
      R = R.v || R;
      const gain = R.votePower * Math.pow(1 - song.E, R.resistance);
      song.E = Math.min(1, song.E + gain);
      const others = songs.filter((o) => o !== song && !o.locked && o.E > 0);
      let tot = others.reduce((s, o) => s + o.E, 0);
      if (tot > 0 && R.steal > 0) {
        const take = Math.min(tot, gain * R.steal);
        others.forEach((o) => (o.E = Math.max(0, o.E - (take * o.E) / tot)));
      }
      tot = others.reduce((s, o) => s + o.E, 0);
      if (song.E > R.poolCap) song.E = R.poolCap;
      if (song.E + tot > R.poolCap && tot > 0) {
        const k = Math.max(0, R.poolCap - song.E) / tot;
        others.forEach((o) => (o.E *= k));
      }
      song.lastVote = now;
      song.heat = (song.heat || 0) + 1;
      return gain;
    },
    decay(songs, dt, R, now, skip) {
      R = R.v || R;
      for (const a of songs) {
        if (a.locked || (skip && skip(a))) continue;
        if (a.heat > 0 && now - a.lastVote > R.decayDelay) {
          a.heat *= Math.pow(0.5, dt / Math.max(0.1, R.halfLife));
          if (a.heat < 0.05) a.heat = 0;
        }
        if (a.E <= 0) continue;
        if (now - a.lastVote > R.decayDelay) {
          const rate = Math.max((a.E * Math.LN2) / Math.max(0.1, R.halfLife), R.minFade);
          a.E = Math.max(0, a.E - rate * dt);
        }
      }
    },
    /* Sets song.lit, song.level (0..1 display level), song.rel (energy vs the leader, for dimming). */
    levels(songs, R, busy) {
      R = R.v || R;
      const live = songs.filter((a) => !a.locked),
        maxE = live.reduce((m, a) => Math.max(m, a.E), 0),
        vAt = energy.lightThreshold(R),
        span = Math.max(0.01, R.fullAt - vAt);
      live.forEach((a) => {
        if ((a.heat || 0) >= Math.round(R.lightUpVotes) - 1e-6) a.armed = true;
        if (a.E < 0.01 || (a.heat || 0) < 0.5) a.armed = false;
      });
      const cand = live.filter((a) => a.armed && !(busy && busy(a))).sort((x, y) => y.E - x.E),
        NH = Math.round(R.hotSlots),
        eN = cand.length >= NH ? cand[NH - 1].E : 0;
      songs.forEach((a) => {
        a.lit = false;
      });
      cand.forEach((a, i) => {
        if (i < NH || a.E >= eN * (1 - R.tieMargin)) a.lit = true;
      });
      for (const a of songs) {
        const rel = maxE > 0 ? a.E / maxE : 0,
          eAdj = a.E * (1 - R.leaderDim * (1 - rel));
        a.level = a.locked || !a.lit ? 0 : Math.pow(clamp((eAdj - vAt) / span, 0, 1), R.curve);
        a.rel = 1 - (1 - rel) * Math.min(1, maxE / 0.3);
      }
      return { maxE, threshold: vAt };
    },
    /* ---- crowd model: build levels come from how many different people back a song, scaled to the room ----
     Each person who voted for a song in the last crowdWindowSec counts as 1 (fading out over crowdFadeSec).
     Each extra vote from the same person adds repeatWeight more (up to repeatCap extra votes): die-hards help a little. */
    isCrowd(R) {
      R = R.v || R;
      return (R.energyModel || 'crowd') !== 'classic';
    },
    /* People needed for each level (1–5) in a room of `roomSize` people. */
    needs(roomSize, R) {
      R = R.v || R;
      const V = Math.max(0, roomSize || 0),
        ref = Math.max(1, R.bigRoomRef || 100),
        disc = V > ref ? Math.pow(V / ref, -(R.bigRoomShrink || 0)) : 1,
        out = [];
      for (let L = 1; L <= 5; L++)
        out.push({
          level: L,
          allowed: V >= (R['lvl' + L + 'Room'] || 0),
          need: Math.max(
            R['lvl' + L + 'Min'] || 1,
            Math.ceil((R['lvl' + L + 'Pct'] || 0) * V * disc),
          ),
        });
      for (let i = 1; i < 5; i++) out[i].need = Math.max(out[i].need, out[i - 1].need + 1);
      return out;
    },
    /* Record one vote by `person` (any stable id) for `song`. */
    recordVote(song, person, now, R) {
      R = R.v || R;
      const m = song.voters || (song.voters = new Map()),
        e = m.get(person),
        lim = (R.crowdWindowSec || 180) + (R.crowdFadeSec || 0);
      if (!e || now - e.t > lim) m.set(person, { n: 1, t: now });
      else {
        e.n++;
        e.t = now;
      }
      song.lastVote = now;
    },
    /* Current backing for a song: {S: support, people, repeats}. Old votes are dropped. */
    support(song, now, R) {
      R = R.v || R;
      const m = song.voters;
      if (!m) return { S: 0, people: 0, repeats: 0 };
      const W = R.crowdWindowSec || 180,
        F = Math.max(0.001, R.crowdFadeSec || 0),
        cap = R.repeatCap || 0,
        rw = R.repeatWeight || 0;
      let S = 0,
        people = 0,
        rep = 0;
      for (const [k, e] of m) {
        const age = now - e.t;
        if (age > W + F) {
          m.delete(k);
          continue;
        }
        const w = age < W ? 1 : Math.max(0, 1 - (age - W) / F);
        if (w <= 0) continue;
        const x = Math.min(e.n - 1, cap);
        people += w;
        rep += x * w;
        S += w * (1 + rw * x);
      }
      return { S, people, repeats: rep };
    },
    /* Sets song.lit, song.level (0..1 for setBuild), song.stage (0..5, the build level), song.capped, song.rel. */
    crowdLevels(songs, roomSize, now, R, busy) {
      R = R.v || R;
      const needs = energy.needs(roomSize, R);
      let maxAllowed = 0;
      needs.forEach((n) => {
        if (n.allowed) maxAllowed = n.level;
      });
      for (const a of songs) {
        const sp =
            a.holdSupport != null
              ? { S: a.holdSupport, people: a.holdSupport, repeats: 0 }
              : energy.support(a, now, R),
          S = sp.S;
        a.support = S;
        a.people = sp.people;
        a.repeats = sp.repeats;
        let st = 0;
        if (maxAllowed > 0 && S >= needs[0].need) {
          st = 1;
          for (let i = 1; i < maxAllowed; i++) {
            if (S >= needs[i].need) {
              st = i + 1;
              continue;
            }
            st = i + (S - needs[i - 1].need) / (needs[i].need - needs[i - 1].need);
            break;
          }
          if (st >= maxAllowed && maxAllowed < 5) {
            const top = needs[maxAllowed - 1].need;
            st = maxAllowed + clamp((S - top) / Math.max(1, top), 0, 1) * (R.capBoost || 0);
          }
        }
        a.stage = st;
        a.capped = maxAllowed < 5 && st >= maxAllowed;
      }
      const live = songs.filter((a) => !a.locked),
        maxS = live.reduce((m, a) => Math.max(m, a.support || 0), 0);
      const cand = live
          .filter((a) => a.stage >= 1 && !(busy && busy(a)))
          .sort((x, y) => y.support - x.support),
        NH = Math.round(R.hotSlots),
        sN = cand.length >= NH ? cand[NH - 1].support : 0;
      songs.forEach((a) => {
        a.lit = false;
      });
      cand.forEach((a, i) => {
        if (i < NH || a.support >= sN * (1 - R.tieMargin)) a.lit = true;
      });
      for (const a of songs) {
        a.level = a.locked || !a.lit ? 0 : clamp((a.stage - 1) / 4, 0, 1);
        a.rel =
          maxS > 0
            ? 1 - (1 - (a.support || 0) / maxS) * Math.min(1, maxS / Math.max(1, needs[0].need * 3))
            : 1;
      }
      return { needs, maxAllowed, roomSize };
    },
    clear(song) {
      song.voters = null;
      song.E = 0;
      song.heat = 0;
      song.armed = false;
    },
    cooldownLeft(lastVoteTime, now, R) {
      R = R.v || R;
      if (!R.cooldownOn) return 0;
      return Math.max(0, R.cooldownSec - (now - (lastVoteTime ?? -1e9)));
    },
  };

  /* ===================== events ===================== */
  function emitter() {
    const m = {};
    return {
      on(t, f) {
        (m[t] || (m[t] = [])).push(f);
        return () => {
          m[t] = (m[t] || []).filter((x) => x !== f);
        };
      },
      emit(t, d) {
        (m[t] || []).slice().forEach((f) => {
          try {
            f(d);
          } catch (e) {
            console.error(e);
          }
        });
        (m['*'] || []).slice().forEach((f) => {
          try {
            f(t, d);
          } catch (e) {
            console.error(e);
          }
        });
      },
    };
  }

  /* ===================== FLIP helper (reorder animation for the queue) ===================== */
  /* getEls(): the card elements; mutate(): reorder the DOM. Animates every moved card from its old spot. */
  function flip(getEls, mutate, o = {}) {
    const before = new Map(
      getEls().map((el) => [el, el.isConnected ? el.getBoundingClientRect() : null]),
    );
    mutate();
    const anims = [];
    for (const el of getEls()) {
      const f = before.get(el);
      if (!f) continue;
      const l = el.getBoundingClientRect();
      const dx = f.left - l.left,
        dy = f.top - l.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
      const focus = el === o.focus;
      anims.push(
        el.animate([{ transform: `translate(${dx}px,${dy}px)` }, { transform: 'translate(0,0)' }], {
          duration: focus
            ? o.durationMs || 600
            : o.othersDurationMs || (o.durationMs || 600) * 0.75,
          easing: focus ? o.easing || EASES.spring : o.othersEasing || EASES.smooth,
        }).finished,
      );
    }
    return Promise.all(anims).then(
      () => {},
      () => {},
    );
  }

  /* ===================== stage and cards ===================== */
  let UID = 1;
  class Card {
    constructor(stage, o) {
      this.stage = stage;
      this.uid = UID++;
      this.el = o.el;
      this.inner = o.inner || o.el;
      this.cover = o.cover || this.inner;
      this.art = o.art || null;
      this.id = o.id != null ? o.id : this.uid;
      this.phase = Math.random() * TAU;
      this.stars = [];
      for (let j = 0; j < 40; j++)
        this.stars.push({
          u: Math.random(),
          d: 0.2 + Math.random() * 0.7,
          ph: Math.random() * TAU,
        });
      this.L = { build: mkRunner(this, 0), ag: mkRunner(this, 1.7) };
      this.in = { level: 0, visible: 0, lit: true, rel: 1, instant: false };
      this.disp = 0;
      this.visD = 0;
      this.kick = 0;
      this.locked = !!o.locked;
      this.after = null;
      this.agView = null;
      /* per-card vibe set override (a boosted song in its booster's look); null uses the stage's */
      this.V = null;
      this.bst = null;
      this.lastB = null;
      this.acc = {};
      this.rect = null;
      this.clip = null;
      this.jx = this.jy = this.jr = this.jvx = this.jvy = this.jvr = this.jfl = this.jring = 0;
      this.dip = 0;
      this.sq = 0;
      this.vx = this.vy = 0;
      this.sfr = 0;
      this.sliding = false;
      this._buildOn = false;
      this._settled = false;
      this.floats = [];
      if (this.el && this.el.classList) this.el.classList.add('ssfx-card');
      if (this.inner && this.inner !== this.el && this.inner.classList)
        this.inner.classList.add('ssfx-card-inner');
    }
    /* ---- energy build (the viewer's vibe set) ---- */
    setBuild(o = {}) {
      if (typeof o === 'number') o = { level: o };
      const i = this.in;
      if (o.level != null) i.level = clamp(+o.level || 0, 0, 1);
      i.visible = o.visible != null ? clamp(+o.visible, 0, 1) : o.level != null ? 1 : i.visible;
      if (o.lit != null) i.lit = !!o.lit;
      if (o.rel != null) i.rel = clamp(+o.rel, 0, 1);
      i.instant = !!o.instant;
      return this;
    }
    clearBuild(o = {}) {
      this.in.visible = 0;
      this.in.instant = !!o.instant;
      return this;
    }
    /* ---- per-card vibe set ----
       Override the stage's vibe set for this card only: its build, afterglow, tier thresholds and #1
       topColor. Accepts what stage.setVibe takes; null returns the card to the stage's vibe set. */
    setVibe(x) {
      this.V = x == null ? null : x && x.v && x.col ? x : compileAs(x, 'vibe');
      return this;
    }
    /* the vibe set this card draws with */
    get vibe() {
      return this.V || this.stage.V;
    }
    vote(o = {}) {
      const R = this.stage.R,
        V = this.vibe,
        mine = !!o.mine;
      this.kick = Math.max(this.kick, mine ? 1 : 0.45);
      if (this.rect && this.L.build.S) {
        const crr = cardRR(this.rect),
          cL = rrLen(crr),
          n = Math.round(V.v.kickSparks * (mine ? 1 : 0.35));
        for (let i = 0; i < n; i++) {
          const p = rrPoint(crr, Math.random() * cL);
          spawnSpark(this.L.build, this.L.build.S, p.x, p.y, p.nx, p.ny);
        }
      }
      if (R.v.plusOne && (o.showPlusOne != null ? o.showPlusOne : mine))
        this.floats.push({ t0: NOW || performance.now() / 1000 });
      this._emit('vote', { mine });
      return this;
    }
    /* Tap feedback for a song that can't take votes (e.g. the highest open spot): a short word that moves like the +1. */
    notice(text) {
      const R = this.stage.R.v;
      if (R.upBlockedOn === false) return this;
      const t = String(text || R.upBlockedText || 'Top spot');
      this.floats.push({ t0: NOW || performance.now() / 1000, text: t });
      this._emit('notice', { text: t });
      return this;
    }
    setLocked(b) {
      this.locked = !!b;
      return this;
    }
    /* ---- afterglow (the viewer's vibe set) ---- */
    tierFor(level) {
      const V = this.vibe.v,
        st = 1 + Math.round(clamp(level || 0, 0, 1) * 4);
      return st <= V.tierSmallMax ? 0 : st <= V.tierMedMax ? 1 : 2;
    }
    startAfterglow(o = {}) {
      const tier = o.tier != null ? clamp(o.tier | 0, 0, 2) : this.tierFor(o.fromLevel || 0);
      this.after = {
        t0: this.stage.now,
        s0: this.stage.simTime,
        tier,
        color: o.color ? hexToRgb(o.color) : null,
        fade0: this.stage.now,
      };
      this._settled = false;
      this._emit('afterglow-start', { tier });
      return this;
    }
    /* also drops the per-card vibe set: the card returns to the stage's */
    clearAfterglow() {
      this.V = null;
      if (this.after) {
        this.after = null;
        this._emit('afterglow-end', {});
      }
      return this;
    }
    /* Editor hook: show a fixed afterglow keyframe blend (i→j by f). Pass null to return to the normal timeline. */
    setAfterglowView(v) {
      this.agView = v || null;
      return this;
    }
    get busy() {
      return !!this.bst;
    }
    get boostPhase() {
      return this.bst ? this.bst.phase : null;
    }
    /* ---- boost animation (the booster's package, seen by every viewer) ----
       o.vibe: the booster's vibe set. The card switches to it at boost-end, before its afterglow
       starts, so the viewer's own look never flashes in between. */
    boost(pkg, o = {}) {
      if (this.bst) return Promise.reject(new Error('SongShakeFX: this card is already boosting'));
      const S = this.stage,
        B = pkg && pkg.v && pkg.col ? pkg : compileAs(pkg, 'boost');
      const level = o.level != null ? o.level : this.L.build.vis > 0.05 ? this.L.build.D : 0;
      let dir = o.direction;
      if (dir == null && o.target) {
        const a = this.el.getBoundingClientRect(),
          b = o.target.getBoundingClientRect();
        dir = Math.sign(b.left - a.left) || -1;
      }
      const bst = (this.bst = {
        B,
        phase: 'charge',
        chargeT0: S.now,
        dir: dir || -1,
        level,
        energy: o.energy != null ? clamp(o.energy, 0, 1) : level,
        isTop: !!o.toTop,
        opts: o,
      });
      this.lastB = B;
      this.boostDir = bst.dir;
      this._emit('boost-start', { level, tier: this.tierFor(level) });
      return new Promise((resolve) => {
        bst.resolve = resolve;
        const T = (ms, f) =>
          setTimeout(
            () => {
              if (this.bst === bst && !this._dead) f();
            },
            Math.max(0, ms),
          );
        T(B.v.chargeMs, () => {
          bst.phase = 'release';
          bst.releaseT = S.now;
          S._release(this, bst);
          this.in.visible = 0;
          this.kick = 0;
          if (this._buildOn) {
            this._buildOn = false;
            this._emit('build-end', { reason: 'boost' });
          }
          this._emit('boost-release', {});
          if (o.onRelease)
            try {
              o.onRelease();
            } catch (e) {
              console.error(e);
            }
          T(B.v.slideDelay, () => {
            bst.phase = 'move';
            this.sliding = true;
            this.slideT0 = S.now;
            if (this.el) this.el.classList.add('ssfx-moving');
            const info = {
              card: this,
              durationMs: B.v.slideMs,
              easing: EASES[B.v.slideEase] || EASES.smooth,
              easingName: B.v.slideEase,
            };
            this._emit('boost-move', { durationMs: info.durationMs, easing: info.easing });
            const p = o.move ? o.move(info) : new Promise((r) => setTimeout(r, B.v.slideMs));
            Promise.resolve(p)
              .catch(() => {})
              .then(() => T(o.settleMs != null ? o.settleMs : 150, () => this._endBoost()));
          });
        });
      });
    }
    _endBoost() {
      const bst = this.bst;
      if (!bst) return;
      this.sliding = false;
      if (this.el) this.el.classList.remove('ssfx-moving');
      this.bst = null;
      if (bst.opts.vibe != null) this.setVibe(bst.opts.vibe);
      const tier = this.tierFor(bst.level),
        color = rgbHex(bst.B.col.burstColor || [255, 231, 141]);
      this._emit('boost-end', { tier, level: bst.level, color });
      if (bst.opts.afterglow !== false) this.startAfterglow({ tier, color });
      bst.resolve && bst.resolve({ tier, level: bst.level, color });
    }
    cancelBoost() {
      if (this.bst) {
        this.bst.opts.afterglow = false;
        this._endBoost();
      }
    }
    _emit(type, detail) {
      detail = Object.assign({ card: this, id: this.id }, detail);
      this.stage._ev.emit(type, detail);
      if (this.el && typeof CustomEvent === 'function')
        this.el.dispatchEvent(new CustomEvent('ssfx:' + type, { bubbles: true, detail }));
    }
  }
  const rgbHex = (c) =>
    '#' + c.map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('');

  class Stage {
    constructor(o = {}) {
      if (!o.container) throw new Error('SongShakeFX.createStage needs {container}');
      this.container = o.container;
      this.container.classList.add('ssfx-stage');
      const mk = (cls) => {
        const c = document.createElement('canvas');
        c.className = 'ssfx-layer ' + cls;
        c.setAttribute('aria-hidden', 'true');
        return c;
      };
      this.back = o.back || mk('ssfx-back');
      this.front = o.front || mk('ssfx-front');
      if (!o.back) this.container.insertBefore(this.back, this.container.firstChild);
      if (!o.front) this.container.appendChild(this.front);
      this.bctx = this.back.getContext('2d');
      this.fctx = this.front.getContext('2d');
      this.safeTop =
        typeof o.safeTop === 'function'
          ? o.safeTop
          : o.avoid
            ? () =>
                o.avoid.getBoundingClientRect().bottom -
                this.container.getBoundingClientRect().top +
                1
            : () => 0;
      this.shakeEl = o.shakeEl || null;
      this.cards = [];
      this.bursts = [];
      this.trail = [];
      this.rk = [];
      this.rks = [];
      this.rsl = [];
      this.smoke = [];
      this.hits = [];
      this.V = compileAs(o.vibe || null, 'vibe');
      this.R = compileAs(o.rules || o.platform || null, 'rules');
      this.now = performance.now() / 1000;
      this.simTime = 0;
      this.timeScale = 1;
      this.debugBounds = !!o.debugBounds;
      this.shakeAmp = 0;
      this.dpr = 1;
      this.w = 0;
      this.h = 0;
      this.maxDpr = o.maxDpr || 2;
      this._ev = emitter();
      this.reduced = false;
      try {
        const mq = matchMedia('(prefers-reduced-motion: reduce)');
        this.reduced = o.reducedMotion != null ? !!o.reducedMotion : mq.matches;
      } catch (e) {}
      this._ro =
        typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.resize()) : null;
      if (this._ro) this._ro.observe(this.container);
      this.resize();
      this._raf = 0;
      this._last = 0;
      if (o.autoStart !== false) this.start();
    }
    /* which vibe set this viewer sees; which effect rules apply */
    setVibe(x) {
      this.V = x && x.v && x.col ? x : compileAs(x, 'vibe');
      return this;
    }
    setRules(x) {
      this.R = x && x.v && x.col ? x : compileAs(x, 'rules');
      return this;
    }
    setPlatform(x) {
      return this.setRules(x);
    }
    addCard(o) {
      const c = new Card(this, o);
      this.cards.push(c);
      return c;
    }
    removeCard(c) {
      c._dead = true;
      this.cards = this.cards.filter((x) => x !== c);
      if (c.inner) c.inner.style.transform = '';
      if (c.cover) c.cover.style.filter = '';
      if (c.el) c.el.style.zIndex = '';
    }
    on(type, fn) {
      return this._ev.on(type, fn);
    }
    resize() {
      this.dpr = Math.min(this.maxDpr, window.devicePixelRatio || 1);
      const r = this.container.getBoundingClientRect();
      this.w = r.width;
      this.h = r.height;
      for (const c of [this.back, this.front]) {
        c.width = Math.max(1, Math.round(this.w * this.dpr));
        c.height = Math.max(1, Math.round(this.h * this.dpr));
      }
      this.bctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.fctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }
    start() {
      if (this._raf) return;
      const loop = (ts) => {
        this._raf = requestAnimationFrame(loop);
        this.tick(ts);
      };
      this._raf = requestAnimationFrame(loop);
    }
    stop() {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
    destroy() {
      this.stop();
      if (this._ro) this._ro.disconnect();
      this.cards.slice().forEach((c) => this.removeCard(c));
      this.back.remove();
      this.front.remove();
    }
    /* Advance and draw one frame. Call yourself with autoStart:false, otherwise it runs on requestAnimationFrame. */
    tick(ts) {
      ts = ts == null ? performance.now() : ts;
      const t = ts / 1000,
        rdt = this._last ? Math.min(0.05, Math.max(0, t - this._last)) : 0;
      this._last = t;
      this.now = t;
      this.simTime += rdt * this.timeScale;
      DPR = this.dpr;
      NOW = t;
      SW = this.w;
      SH = this.h;
      try {
        this._update(rdt, t);
        this._draw(rdt, t);
      } catch (e) {
        console.error(e);
      }
    }
    _release(card, bst) {
      const B = bst.B,
        v = B.v,
        col = B.col,
        r = Object.assign({}, card.rect || { x: 0, y: 0, w: 0, h: 0 }),
        cx = r.x + r.w / 2,
        cy = r.y + r.h / 2;
      let scale = (1 - v.energyScaling + v.energyScaling * bst.energy) * v.releaseIntensity;
      if (bst.isTop) scale *= v.topBoost;
      const pal = [col.sprC1, col.sprC2, col.sprC3, col.sprC4, col.sprC5];
      const b = {
        t0: this.now,
        r,
        clip: cardRR(r, v.releaseMargin),
        parts: [],
        inten: Math.min(1.5, scale),
        bolts: Math.round(v.releaseBolts * Math.min(2, scale)),
        seed: Math.random() * 1000,
        B,
      };
      const n = Math.round(v.burstCount * scale);
      for (let i = 0; i < n; i++) {
        const ang = Math.random() * TAU,
          sp = v.burstSpeed * Math.sqrt(scale) * (0.25 + Math.random() * 0.85),
          life = v.burstLife * (0.5 + Math.random() * 0.7);
        const sl = pickSlot(v, 'burst');
        if (!sl) break;
        if (sl.id !== 'spark') {
          b.parts.push({
            k: 'shape',
            id: sl.id,
            own: sl.own,
            ci: (Math.random() * 5) | 0,
            pal,
            x: cx + Math.cos(ang) * r.w * 0.3,
            y: cy + Math.sin(ang) * r.h * 0.3,
            vx: Math.cos(ang) * sp,
            vy: Math.sin(ang) * sp,
            life,
            max: life,
            g: v.burstGravity,
            w: v.burstWind,
            drag: 0.045,
            c0: col.burstColor,
            c1: col.burstEndColor,
            size: v.burstSize * 2.2 * sl.sc * (0.75 + Math.random() * 0.5),
            rot: Math.random() * TAU,
            spin: (Math.random() * 2 - 1) * 5,
            op: 0.75 + Math.random() * 0.25,
            glow: 0.5,
            grow: 0,
          });
          continue;
        }
        b.parts.push({
          k: 'burst',
          x: cx + Math.cos(ang) * r.w * 0.3,
          y: cy + Math.sin(ang) * r.h * 0.3,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          life,
          max: life,
          g: v.burstGravity,
          w: v.burstWind,
          drag: 0.045,
          c0: col.burstColor,
          c1: col.burstEndColor,
          core: v.burstSize * sl.sc * (0.4 + Math.random() * 0.5),
          gs: v.burstSize * sl.sc * 2.4,
          go: 0.55,
          b: 0.7 + Math.random() * 0.3,
          st: 0.015,
        });
      }
      this.bursts.push(b);
      if (!this.reduced) this.shakeAmp = v.screenShake * Math.min(1.5, scale);
      if (v.reactOn)
        for (const o of this.cards) {
          if (o === card || !o.rect) continue;
          const ox = o.rect.x + o.rect.w / 2,
            oy = o.rect.y + o.rect.h / 2,
            d = Math.hypot(ox - cx, oy - cy),
            f = Math.exp(-d / v.reactReach) * Math.min(1.5, scale);
          if (f < 0.02) continue;
          this.hits.push({
            o,
            at: this.now + d / v.reactSpeed,
            f,
            dx: (ox - cx) / (d || 1),
            dy: (oy - cy) / (d || 1),
            B,
          });
        }
    }
    _update(rdt, t) {
      const cr0 = this.container.getBoundingClientRect();
      STRIP = this.safeTop();
      const V0 = this.V,
        R = this.R,
        Rv = R.v,
        M = Rv.effectMargin,
        hin = Math.max(1, Rv.handoffInMs || 150) / 1000,
        hout = Math.max(1, Rv.handoffOutMs || 400) / 1000;
      const k = Rv.responseMs <= 0 ? 1 : 1 - Math.exp((-rdt * 1000) / Rv.responseMs);
      for (let i = this.hits.length - 1; i >= 0; i--) {
        const h = this.hits[i];
        if (this.now < h.at) continue;
        this.hits.splice(i, 1);
        const o = h.o,
          W = TAU * 4,
          hv = h.B.v,
          push = this.reduced ? 0 : 1;
        o.jvx += h.dx * hv.reactPush * h.f * W * push;
        o.jvy += h.dy * hv.reactPush * h.f * W * 0.5 * push;
        o.jvr += (h.dx >= 0 ? 1 : -1) * hv.reactTilt * h.f * W * push;
        o.reactBounce = hv.reactBounce;
        o.jfl = Math.max(o.jfl, hv.reactFlash * Math.min(1, h.f));
        o.jring = Math.max(o.jring, Math.min(1, h.f));
        o.ringOp = hv.reactRing;
        o.ringC = h.B.col.reactRingColor;
      }
      const movers = this.cards.filter(
        (a) => a.bst && (a.sliding || a.bst.phase === 'charge') && a.rect,
      );
      for (const a of this.cards) {
        if (a.el && !a.el.isConnected) {
          a.rect = null;
          continue;
        }
        const cr = a.cover.getBoundingClientRect(),
          r = (a.rect = { x: cr.left - cr0.left, y: cr.top - cr0.top, w: cr.width, h: cr.height });
        a.clip = cardRR(r, M);
        if (a.sliding) {
          a.sfr++;
          if (a.sfr > 2 && a.lastR && rdt > 0) {
            const vx = (r.x - a.lastR.x) / rdt,
              vy = (r.y - a.lastR.y) / rdt;
            if (Math.abs(vx) < 6000 && Math.abs(vy) < 6000) {
              const q = Math.min(1, rdt * 18);
              a.vx = lerp(a.vx, vx, q);
              a.vy = lerp(a.vy, vy, q);
            }
          }
        } else {
          a.sfr = 0;
          a.vx = 0;
          a.vy = 0;
        }
        a.lastR = { x: r.x, y: r.y };
        const V = a.V || V0,
          Vv = V.v,
          bst = a.bst,
          B = bst && bst.B;
        // build target
        let target = a.in.level,
          vt = a.in.visible,
          cp = 0;
        if (bst && bst.phase === 'charge') {
          cp = clamp((this.now - bst.chargeT0) / Math.max(0.001, B.v.chargeMs / 1000), 0, 1);
          target = lerp(target, 1, easeIn(cp) * 0.7 + cp * 0.3);
          vt = 1;
        }
        if (a.in.instant && !bst) {
          a.disp = target;
          a.visD = vt;
        } else {
          const kk = bst && bst.phase === 'charge' ? Math.max(k, 0.3) : k;
          a.disp += (target - a.disp) * kk;
          a.visD += (vt - a.visD) * kk;
        }
        let buildFade = 1;
        if (bst && bst.releaseT != null)
          buildFade = clamp(1 - (this.now - bst.releaseT) / hin, 0, 1);
        if (bst && bst.releaseT != null && buildFade <= 0) {
          a.disp = 0;
          a.visD = 0;
        }
        a.kick *= Math.exp(-rdt * 5);
        const kickAdd = a.kick * Vv.kickFlash,
          Lb = a.L.build,
          Lg = a.L.ag;
        Lb.rect = r;
        Lg.rect = r;
        Lb.D = clamp(a.disp + kickAdd * 0.3, 0, 1);
        Lb.vis = clamp(Math.max(a.visD, !a.locked ? kickAdd * 0.8 : 0) * buildFade, 0, 1);
        if (!a._buildOn && Lb.vis > 0.02 && a.in.visible > 0) {
          a._buildOn = true;
          a._emit('build-start', { level: a.in.level });
        } else if (a._buildOn && a.in.visible <= 0 && Lb.vis < 0.01) {
          a._buildOn = false;
          a._emit('build-end', { reason: 'cleared' });
        }
        // afterglow
        let gi = 4,
          gj = 4,
          gf = 0,
          gvt = 0,
          cool = 0,
          instant = false;
        if (a.agView && !bst) {
          const w = a.agView;
          gi = w.i;
          gj = w.j != null ? w.j : w.i;
          gf = w.f || 0;
          gvt = w.visible != null ? w.visible : 1;
          cool = w.cool || 0;
          instant = !!w.instant;
        } else if (a.after) {
          const el = this.simTime - a.after.s0,
            cf = Math.pow(clamp(el / Math.max(1, Vv.afterCoolSec), 0, 1), Vv.afterCurve),
            sp = Vv.afterSettlePoint;
          cool = cf;
          gvt = bst ? 0 : 1;
          if (cf < sp) {
            gi = a.after.tier;
            gj = 3;
            gf = cf / sp;
          } else {
            gi = 3;
            gj = 4;
            gf = (cf - sp) / Math.max(0.001, 1 - sp);
          }
          if (cf >= 1 && !a._settled) {
            a._settled = true;
            a._emit('afterglow-settled', {});
          }
        }
        a.afterCool = cool;
        if (instant) Lg.visD = gvt;
        else {
          const kk = gvt > Lg.visD ? Math.min(1, rdt / (hout / 3)) : Math.max(k, 0.15);
          Lg.visD += (gvt - Lg.visD) * kk;
        }
        Lg.vis = clamp(Lg.visD, 0, 1);
        Lg.D = 0;
        Lb.S = evalLook(V, Lb.D);
        Lg.S = evalBetween(V, gi, gj, gf);
        Lb.amp = ampOf(Lb, t, 1 + kickAdd * 0.5);
        Lg.amp = ampOf(Lg, t, 1);
        // card motion
        const Sb = Lb.S,
          Sg = Lg.S,
          vb = Lb.vis,
          vg = Lg.vis,
          ph = a.phase,
          tr = this.reduced ? 0 : 1;
        let tx = 0,
          ty = -(Sb.lift * vb + Sg.lift * vg),
          sc = 1 + Sb.grow * vb + Sg.grow * vg + (a.sliding ? 0.06 : 0);
        const tw = (amt, hz, q) =>
          amt > 0
            ? amt *
              tr *
              (Math.sin(t * TAU * hz + q) * 0.6 + Math.sin(t * TAU * hz * 1.73 + q * 1.9) * 0.4)
            : 0;
        const cA = cp * (B ? B.v.chargeTremble : 0);
        tx +=
          tw(Sb.tremble * vb, Sb.trembleSpeed, ph) +
          tw(Sg.tremble * vg, Sg.trembleSpeed, ph + 0.5) +
          tw(cA, 18, ph + 1);
        ty +=
          tw(Sb.tremble * vb, Sb.trembleSpeed * 1.13, ph + 2) +
          tw(Sg.tremble * vg, Sg.trembleSpeed * 1.13, ph + 2.5) +
          tw(cA, 21, ph + 3);
        const J = this._motion(a, rdt, r, movers);
        tx += J.x;
        ty += J.y;
        a.inner.style.transform =
          Math.abs(tx) > 0.004 ||
          Math.abs(ty) > 0.004 ||
          Math.abs(sc - 1) > 1e-4 ||
          Math.abs(J.rot) > 1e-3 ||
          Math.abs(J.sx - 1) > 1e-4 ||
          Math.abs(J.sy - 1) > 1e-4
            ? `translate(${tx.toFixed(2)}px,${ty.toFixed(2)}px) rotate(${J.rot.toFixed(3)}deg) scale(${(sc * J.sx).toFixed(4)},${(sc * J.sy).toFixed(4)})`
            : '';
        let bright = 1 + (J.bright || 0),
          sat = 1;
        if (!a.locked && a.in.lit) bright -= Vv.coverDim * (1 - a.in.rel);
        if (!a.in.lit && !a.locked && !a.after && !a.agView && !bst) {
          bright -= Vv.ineligibleDim;
          sat -= Vv.ineligibleDim * 0.8;
        }
        a.cover.style.filter =
          Math.abs(bright - 1) > 0.001 || sat < 0.999
            ? `brightness(${bright.toFixed(3)}) saturate(${sat.toFixed(3)})`
            : '';
        if (a.el !== a.inner) a.el.style.zIndex = a.sliding ? 5 : Math.max(vb, vg) > 0.05 ? 2 : '';
        const crr = cardRR(r),
          cL = rrLen(crr);
        runEmitters(Lb, rdt, crr, cL);
        runEmitters(Lg, rdt, crr, cL);
        const A = a.acc;
        const emit = (key, rate, fn) => {
          A[key] = (A[key] || 0) + rate * rdt;
          let guard = 0;
          while (A[key] >= 1 && guard++ < 200) {
            A[key]--;
            fn();
          }
        };
        if (bst && cp > 0 && B.v.chargePull > 0)
          emit('pull', B.v.chargePull * cp, () => {
            const d = M * (0.8 + Math.random() * 0.3),
              p = rrPoint(crr, Math.random() * cL),
              x = p.x + p.nx * d,
              y = p.y + p.ny * d;
            Lb.parts.push({
              k: 'spark',
              fixed: 1,
              x,
              y,
              vx: -p.nx * d * 3,
              vy: -p.ny * d * 3,
              life: 0.3,
              max: 0.3,
              g: 0,
              w: 0,
              drag: 0.05,
              c0: B.col.burstColor,
              c1: B.col.burstEndColor,
              gc: B.col.burstColor,
              core: 1,
              gs: 4,
              go: 0.6,
              b: 1,
              st: 0.02,
            });
          });
        if (a.after && !bst && Vv.afterEmbers > 0 && !a.sliding)
          emit('ember', Vv.afterEmbers * Lg.vis, () => {
            const p = rrPoint(crr, Math.random() * cL);
            Lg.parts.push({
              k: 'ember',
              fixed: 1,
              x: p.x,
              y: p.y,
              vx: p.nx * 6 + (Math.random() - 0.5) * 8,
              vy: p.ny * 6 - 8,
              life: 1 + Math.random(),
              max: 2,
              g: -4,
              drag: 0.01,
              size: 0.8 + Math.random() * 0.8,
              c: a.locked ? V.col.topColor : V.col.afterColor,
            });
          });
        if (a.sliding && B.v.trailRate > 0)
          emit('trail', B.v.trailRate, () => {
            this.trail.push({
              k: 'burst',
              x: r.x + Math.random() * r.w,
              y: r.y + Math.random() * r.h,
              vx: (Math.random() - 0.5) * 40,
              vy: (Math.random() - 0.5) * 40,
              life: 0.5 + Math.random() * 0.4,
              max: 0.9,
              g: 40,
              drag: 0.04,
              c0: B.col.burstColor,
              c1: B.col.burstEndColor,
              core: B.v.burstSize * 0.5,
              gs: B.v.burstSize * 2,
              go: 0.5,
              b: 0.9,
              st: 0,
            });
          });
        this._rocket(a, r, rdt, cp, emit);
        stepRunner(Lb, rdt);
        stepRunner(Lg, rdt);
      }
      this.bursts.forEach((b) => stepParts(b.parts, rdt));
      stepParts(this.trail, rdt);
      stepParts(this.rk, rdt);
      stepParts(this.rks, rdt);
      stepParts(this.rsl, rdt);
      stepParts(this.smoke, rdt);
    }
    _motion(a, rdt, r, movers) {
      const W = TAU * 4,
        z = lerp(1, 0.1, a.reactBounce != null ? a.reactBounce : 0.5),
        n = 2,
        dt = rdt / n;
      for (let i = 0; i < n; i++) {
        for (const q of ['x', 'y', 'r']) {
          const xk = 'j' + q,
            vk = 'jv' + q;
          let x = a[xk],
            v = a[vk];
          v += (-W * W * x - 2 * z * W * v) * dt;
          x += v * dt;
          a[xk] = Math.abs(x) < 1e-3 && Math.abs(v) < 1e-2 ? 0 : x;
          a[vk] = a[xk] === 0 ? 0 : v;
        }
      }
      a.jfl *= Math.exp(-rdt * 6);
      a.jring *= Math.exp(-rdt * 4);
      let dipT = 0,
        sqT = 0;
      if (!a.bst)
        for (const m of movers) {
          if (m === a || !m.rect || !m.bst) continue;
          const mv = m.bst.B.v;
          if (!mv.reactOn) continue;
          const d = Math.abs(r.x + r.w / 2 - (m.rect.x + m.rect.w / 2)),
            pt = clamp(1 - d / (r.w * 1.1), 0, 1);
          dipT = Math.max(dipT, pt * mv.passDip);
          sqT = Math.max(sqT, pt * mv.passSquash);
        }
      const q = Math.min(1, rdt * 14);
      a.dip += (dipT - a.dip) * q;
      a.sq += (sqT - a.sq) * q;
      if (a.dip < 1e-3) a.dip = 0;
      if (a.sq < 1e-4) a.sq = 0;
      let rot = a.jr,
        sx = 1,
        sy = 1;
      if (a.sliding && a.bst && a.bst.B.v.streakOn && !this.reduced) {
        const bv = a.bst.B.v,
          d = a.boostDir || -1,
          sn = clamp(Math.max(0, a.vx * d) / 600, 0, 1.2);
        rot += bv.streakLean * d * sn;
        sx = 1 + bv.streakStretch * sn;
        sy = 1 - bv.streakStretch * sn * 0.5;
      }
      return {
        x: a.jx,
        y: a.jy + a.dip,
        rot,
        sx: sx * (1 - a.sq),
        sy: sy * (1 - a.sq),
        bright: a.jfl,
      };
    }
    _exhaust(a, r) {
      const ex = -(a.boostDir || -1),
        ey = 0;
      const cx = r.x + r.w / 2,
        cy = r.y + r.h / 2,
        t = Math.min(
          Math.abs(ex) > 1e-3 ? r.w / 2 / Math.abs(ex) : 1e9,
          Math.abs(ey) > 1e-3 ? r.h / 2 / Math.abs(ey) : 1e9,
        );
      const px = -ey,
        py = ex,
        half = (Math.abs(px) * r.w) / 2 + (Math.abs(py) * r.h) / 2;
      return { ex, ey, bx: cx + ex * t, by: cy + ey * t, px, py, half };
    }
    _rocket(a, r, rdt, cp, emit) {
      const B = a.bst ? a.bst.B : a.lastB;
      if (!B) {
        a.rk = null;
        return;
      }
      const v = B.v,
        col = B.col;
      const fwd = Math.max(0, a.vx * (a.boostDir || -1)),
        sn = a.sliding ? clamp(fwd / 600, 0, 1.5) : 0;
      let pw = 0;
      if (a.sliding) pw = lerp(1, Math.min(1, sn * 1.4), v.tailFollowSpeed);
      else if (a.bst && a.bst.phase === 'charge') pw = v.tailCharge * cp;
      const slideSec = Math.max(0.05, v.slideMs / 1000),
        prog = a.sliding ? (this.now - (a.slideT0 != null ? a.slideT0 : this.now)) / slideSec : 0,
        cut = a.sliding && prog >= v.tailCutoff;
      const fadeK = cut ? clamp(1 - ((prog - v.tailCutoff) * slideSec) / 0.2, 0, 1) : 1;
      const X = this._exhaust(a, r);
      a.rk = { pw: v.tailOn ? pw * fadeK : 0, X, sn, B };
      if (cut) pw = 0;
      if (v.streakOn && a.sliding) {
        (a.hist || (a.hist = [])).push({ x: r.x, y: r.y, w: r.w, h: r.h, t: this.now });
      }
      if (a.hist) {
        while (a.hist.length && a.hist[0].t < this.now - 1.6) a.hist.shift();
        if (
          !a.sliding &&
          a.hist.length &&
          this.now - a.hist[a.hist.length - 1].t > (v.streakCount * v.streakSpacing) / 1000 + 0.05
        )
          a.hist = null;
      }
      if (v.streakOn && a.sliding && !cut && v.speedLines > 0 && sn > 0.08 && !this.reduced)
        emit('sl', v.speedLines * Math.min(1, sn), () => {
          if (this.rsl.length > 300) return;
          const o = (Math.random() * 2 - 1) * X.half * 0.95;
          this.rsl.push({
            x: X.bx + X.px * o,
            y: X.by + X.py * o,
            ex: X.ex,
            ey: X.ey,
            len: v.speedLineLength * Math.min(1, sn),
            life: 0.25,
            max: 0.25,
            vx: 0,
            vy: 0,
            drag: 0,
            c: col.speedLineColor,
          });
        });
      if (!v.tailOn || pw <= 0) return;
      const base = Math.atan2(X.ey, X.ex),
        spr = (v.tailSpread * Math.PI) / 180,
        pal = [col.sprC1, col.sprC2, col.sprC3, col.sprC4, col.sprC5];
      if (v.tailRate > 0)
        emit('tail', v.tailRate * pw, () => {
          if (this.rk.length > 1500) return;
          const o = (Math.random() * 2 - 1) * X.half * v.tailHeight,
            ang = base + (Math.random() * 2 - 1) * spr,
            sp = v.tailSpeed * (0.6 + 0.8 * Math.random()),
            life = v.tailLife * (0.6 + 0.6 * Math.random());
          const sl = pickSlot(v, 'tail');
          if (!sl) return;
          this.rk.push({
            k: 'shape',
            id: sl.id,
            own: sl.own,
            ci: (Math.random() * 5) | 0,
            pal,
            x: X.bx + X.px * o,
            y: X.by + X.py * o,
            vx: Math.cos(ang) * sp,
            vy: Math.sin(ang) * sp,
            life,
            max: life,
            drag: 0.03,
            g: 0,
            size: v.tailSize * sl.sc * Math.max(0.15, 1 + v.tailSizeVar * (Math.random() * 2 - 1)),
            rot: Math.random() * TAU,
            spin: ((v.tailSpin * Math.PI) / 180) * (Math.random() < 0.5 ? -1 : 1),
            op: v.tailOpacity,
            glow: v.tailGlow,
            grow: v.tailGrow,
            c0: col.tailStart,
            c1: col.tailEnd,
          });
        });
      if (v.tailSparks > 0)
        emit('tsp', v.tailSparks * pw, () => {
          if (this.rks.length > 800) return;
          const d = Math.random() * v.tailSpeed * v.tailLife * 0.6,
            o = (Math.random() * 2 - 1) * X.half * v.tailHeight,
            ang = base + (Math.random() - 0.5) * Math.PI * 1.5,
            sp = v.tailSparkSpeed * (0.5 + Math.random()),
            life = 0.3 + Math.random() * 0.35;
          const sl = pickSlot(v, 'ts');
          if (!sl) return;
          const x = X.bx + X.ex * d + X.px * o,
            y = X.by + X.ey * d + X.py * o;
          if (sl.id === 'spark')
            this.rks.push({
              k: 'burst',
              x,
              y,
              vx: Math.cos(ang) * sp,
              vy: Math.sin(ang) * sp,
              life,
              max: life,
              g: 120,
              drag: 0.03,
              c0: col.tailSparkColor,
              c1: col.tailEnd,
              core: 0.9 * sl.sc,
              gs: 3.5 * sl.sc,
              go: 0.6,
              b: 1,
              st: 0.02,
            });
          else
            this.rks.push({
              k: 'shape',
              id: sl.id,
              own: sl.own,
              ci: (Math.random() * 5) | 0,
              pal,
              x,
              y,
              vx: Math.cos(ang) * sp,
              vy: Math.sin(ang) * sp,
              life: life * 1.6,
              max: life * 1.6,
              g: 120,
              drag: 0.03,
              size: 4 * sl.sc,
              rot: Math.random() * TAU,
              spin: (Math.random() * 2 - 1) * 6,
              op: 1,
              glow: 0.5,
              grow: 0,
              c0: col.tailSparkColor,
              c1: col.tailEnd,
            });
        });
      if (v.smokeRate > 0)
        emit('smk', v.smokeRate * pw, () => {
          if (this.smoke.length > 300) return;
          const d = Math.random() * v.tailSpeed * v.tailLife * 0.4,
            o = (Math.random() * 2 - 1) * X.half * v.tailHeight,
            life = v.smokeLife * (0.7 + 0.6 * Math.random());
          this.smoke.push({
            x: X.bx + X.ex * d + X.px * o,
            y: X.by + X.ey * d + X.py * o,
            vx: X.ex * v.tailSpeed * 0.25 + (Math.random() - 0.5) * 20,
            vy: X.ey * v.tailSpeed * 0.25 + (Math.random() - 0.5) * 20,
            g: 0,
            w: 0,
            life,
            max: life,
            drag: 0.04,
            size: v.smokeSize * (0.7 + 0.6 * Math.random()),
            rise: v.smokeRise,
            op: v.smokeOpacity,
            c: col.smokeColor,
          });
        });
    }
    _drawRocket(ctx) {
      const movers = this.cards.filter(
        (a) => a.bst && (a.sliding || a.bst.phase === 'charge') && a.rect,
      );
      const ghosts = this.cards.filter((a) => a.hist && a.hist.length > 1 && a.rect);
      if (
        !this.rk.length &&
        !this.rks.length &&
        !this.rsl.length &&
        !this.smoke.length &&
        !ghosts.length &&
        !movers.some((m) => m.rk && m.rk.pw > 0)
      )
        return;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, STRIP, SW, Math.max(0, SH - STRIP));
      for (const m of new Set([...movers, ...ghosts])) {
        const r = m.rect;
        addRR(ctx, r.x, r.y, r.w, r.h, 10);
      }
      ctx.clip('evenodd');
      ctx.globalCompositeOperation = 'source-over';
      for (const p of this.smoke) {
        const lt = p.life / p.max,
          al = p.op * Math.min(1, (1 - lt) * 6) * lt;
        dsp(ctx, p.x, p.y, p.size * (1 + 1.6 * (1 - lt)), p.c, al);
      }
      for (const a of ghosts) {
        const B = a.bst ? a.bst.B : a.lastB;
        if (!B) continue;
        const v = B.v,
          H = a.hist,
          n = Math.round(v.streakCount);
        if (!n || v.streakOpacity <= 0) continue;
        for (let k = n; k >= 1; k--) {
          const tk = this.now - (k * v.streakSpacing) / 1000;
          if (tk < H[0].t) continue;
          let j = H.length - 1;
          while (j > 0 && H[j - 1].t > tk) j--;
          const h1 = H[Math.max(0, j - 1)],
            h2 = H[j],
            f = h2.t > h1.t ? clamp((tk - h1.t) / (h2.t - h1.t), 0, 1) : 1;
          const g = {
            x: lerp(h1.x, h2.x, f),
            y: lerp(h1.y, h2.y, f),
            w: lerp(h1.w, h2.w, f),
            h: lerp(h1.h, h2.h, f),
          };
          if (Math.hypot(g.x - a.rect.x, g.y - a.rect.y) < 3) continue;
          const al = v.streakOpacity * (1 - (k - 1) / n);
          const art = typeof a.art === 'function' ? a.art() : a.art;
          if (
            v.streakStyle !== 'smear' &&
            art &&
            art.complete !== false &&
            (art.width || art.naturalWidth)
          ) {
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha = clamp(al, 0, 1);
            rr(ctx, g.x, g.y, g.w, g.h, 10);
            ctx.clip();
            ctx.drawImage(art, g.x, g.y, g.w, g.h);
            ctx.restore();
          } else if (v.streakStyle === 'art') {
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = rgba([40, 30, 34], al * 0.8);
            rr(ctx, g.x, g.y, g.w, g.h, 10);
            ctx.fill();
            ctx.restore();
          }
          if (v.streakStyle !== 'art') {
            ctx.save();
            ctx.globalCompositeOperation = 'lighter';
            ctx.fillStyle = rgba(B.col.streakColor, al * 0.6);
            ctx.shadowColor = rgba(B.col.streakColor, al);
            ctx.shadowBlur = 10 * DPR;
            rr(ctx, g.x, g.y, g.w, g.h, 10);
            ctx.fill();
            ctx.restore();
          }
        }
      }
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      for (const p of this.rsl) {
        const lt = p.life / p.max,
          L = p.len * (0.4 + 0.6 * lt),
          g = ctx.createLinearGradient(p.x, p.y, p.x + p.ex * L, p.y + p.ey * L);
        g.addColorStop(0, rgba(p.c, 0.8 * lt));
        g.addColorStop(1, rgba(p.c, 0));
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + p.ex * L, p.y + p.ey * L);
        ctx.stroke();
      }
      for (const m of movers) {
        const Rk = m.rk;
        if (!Rk || Rk.pw <= 0) continue;
        const X = Rk.X,
          v = Rk.B.v,
          col = Rk.B.col;
        ctx.save();
        ctx.translate(X.bx, X.by);
        ctx.rotate(Math.atan2(X.ey, X.ex));
        if (v.plumeOpacity > 0) {
          const fl =
              1 -
              v.plumeFlicker *
                0.5 *
                (1 +
                  0.6 * Math.sin(this.now * 37 + m.phase) +
                  0.4 * Math.sin(this.now * 61 + m.phase * 2)),
            len = Math.max(2, v.plumeLength * Rk.pw * fl),
            w = v.plumeWidth * X.half * 2,
            op = v.plumeOpacity;
          const lay = (ww, ll, c, al) => {
            const g = ctx.createLinearGradient(0, 0, ll, 0);
            g.addColorStop(0, rgba(c, al));
            g.addColorStop(0.55, rgba(c, al * 0.55));
            g.addColorStop(1, rgba(c, 0));
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.moveTo(-3, -ww / 2);
            ctx.quadraticCurveTo(ll * 0.3, -ww * 0.6, ll, 0);
            ctx.quadraticCurveTo(ll * 0.3, ww * 0.6, -3, ww / 2);
            ctx.closePath();
            ctx.fill();
          };
          ctx.shadowColor = rgba(col.plumeOuter, op);
          ctx.shadowBlur = 14 * DPR;
          lay(w, len, col.plumeOuter, op * 0.8);
          ctx.shadowBlur = 0;
          lay(w * 0.45, len * 0.6, col.plumeCore, op);
        }
        const nb = Math.round(v.tailBolts);
        if (nb > 0) {
          const paths = [],
            seed = Math.floor(this.now * 18) * 7.3 + m.uid * 31;
          for (let k = 0; k < nb; k++) {
            const y0 = (hh(seed + k * 3.1) * 2 - 1) * X.half * v.tailHeight * 0.8,
              dy = (hh(seed + k * 5.7) - 0.5) * 0.7,
              L = v.tailBoltLength * Rk.pw * (0.6 + 0.4 * hh(seed + k)),
              nn = Math.hypot(1, dy);
            jagLine(paths, 0, y0, 1 / nn, dy / nn, L, 7, 6, seed + k * 11);
          }
          const al = clamp(Rk.pw, 0, 1);
          ctx.lineJoin = 'round';
          ctx.strokeStyle = rgba(col.tailBoltColor, al * 0.5);
          ctx.lineWidth = 4;
          ctx.shadowColor = rgba(col.tailBoltColor, al);
          ctx.shadowBlur = 12 * DPR;
          paths.forEach((pp) => strokePoly(ctx, pp));
          ctx.shadowBlur = 0;
          ctx.strokeStyle = rgba([255, 255, 255], al);
          ctx.lineWidth = 1.2;
          paths.forEach((pp) => strokePoly(ctx, pp));
        }
        ctx.restore();
      }
      drawParts(ctx, this.rk, null, 0);
      drawParts(ctx, this.rks, null, 0);
      ctx.restore();
    }
    _draw(rdt) {
      const b = this.bctx,
        f = this.fctx,
        V0 = this.V,
        R = this.R,
        M = R.v.effectMargin;
      b.clearRect(0, 0, SW, SH);
      f.clearRect(0, 0, SW, SH);
      for (const a of this.cards) {
        const r = a.rect;
        if (!r || !a.L.build.S) continue;
        const V = a.V || V0,
          Vv = V.v,
          Lb = a.L.build,
          Lg = a.L.ag;
        b.save();
        clipTo(b, a.clip);
        b.globalCompositeOperation = 'lighter';
        let halo = null;
        if (a.after) {
          const s = clamp(((this.now - a.after.t0) / Math.max(1, Vv.afterSettleMs)) * 1000, 0, 1),
            es = easeOut(s),
            top = a.locked ? V.col.topColor : V.col.afterColor,
            start = a.after.color || mix(top, [255, 255, 255], 0.55),
            c = mix(start, top, es);
          const breath =
            1 -
            Vv.afterBreathDepth * 0.5 * (1 + Math.sin(TAU * Vv.afterBreathHz * this.now + a.phase));
          halo = {
            c,
            str:
              lerp(Math.min(1, Vv.afterStrength * 2.2), Vv.afterStrength, es) *
              breath *
              lerp(1, Vv.afterGlowCool, easeOut(a.afterCool || 0)) *
              clamp(Lg.vis * 1.2, 0, 1),
          };
          glow(b, r, c, halo.str, lerp(Math.max(M * 0.8, Vv.afterRadius), Vv.afterRadius, es));
        }
        drawBack(b, Lg, r, M);
        drawBack(b, Lb, r, M);
        b.restore();
        f.save();
        clipTo(f, a.clip);
        f.globalCompositeOperation = 'lighter';
        drawFront(f, Lg, r, M);
        drawFront(f, Lb, r, M);
        if (a.jring > 0.01 && a.ringOp > 0) {
          const R2 = cardRR(r, 2);
          f.strokeStyle = rgba(a.ringC, a.jring * a.ringOp);
          f.lineWidth = 2;
          f.shadowColor = rgba(a.ringC, a.jring);
          f.shadowBlur = 10 * DPR;
          rr(f, R2.x, R2.y, R2.w, R2.h, R2.r);
          f.stroke();
          f.shadowBlur = 0;
        }
        if (halo && Vv.afterBorder > 0) {
          const c = halo.c;
          f.strokeStyle = rgba(c, Vv.afterBorder * halo.str * 1.6);
          f.lineWidth = 1.5;
          f.shadowColor = rgba(c, 0.8);
          f.shadowBlur = 6 * DPR;
          rr(f, r.x - 1.5, r.y - 1.5, r.w + 3, r.h + 3, 11);
          f.stroke();
          f.shadowBlur = 0;
        }
        f.globalCompositeOperation = 'source-over';
        for (let i = a.floats.length - 1; i >= 0; i--) {
          const fl = a.floats[i],
            p = (this.now - fl.t0) / Math.max(0.1, R.v.upDuration);
          if (p >= 1) {
            a.floats.splice(i, 1);
            continue;
          }
          drawPlusOne(f, R, r, clamp(p, 0, 1), fl.text);
        }
        f.restore();
      }
      for (let i = this.bursts.length - 1; i >= 0; i--) {
        const bu = this.bursts[i],
          age = this.now - bu.t0,
          v = bu.B.v,
          col = bu.B.col;
        if (age > 3.5 && !bu.parts.length) {
          this.bursts.splice(i, 1);
          continue;
        }
        f.save();
        clipTo(f, bu.clip);
        f.globalCompositeOperation = 'lighter';
        const p = age / (v.shockMs / 1000);
        if (p < 1 && v.shockOpacity > 0) {
          const e = easeOut(p),
            R2 = cardRR(bu.r, e * v.releaseMargin);
          f.strokeStyle = rgba(col.shockColor, (1 - p) * v.shockOpacity * Math.min(1, bu.inten));
          f.lineWidth = 4 * (1 - p) + 0.5;
          f.shadowColor = rgba(col.shockColor, 1);
          f.shadowBlur = 12 * DPR;
          rr(f, R2.x, R2.y, R2.w, R2.h, R2.r);
          f.stroke();
          f.shadowBlur = 0;
        }
        if (age < 0.35 && v.flash > 0) {
          const cx = bu.r.x + bu.r.w / 2,
            cy = bu.r.y + bu.r.h / 2,
            R0 = Math.max(bu.r.w, bu.r.h),
            fl = v.flash * bu.inten * (1 - age / 0.35);
          const g = f.createRadialGradient(cx, cy, 0, cx, cy, R0);
          g.addColorStop(0, `rgba(255,255,255,${clamp(fl, 0, 1).toFixed(3)})`);
          g.addColorStop(0.5, rgba(col.burstColor, fl * 0.5));
          g.addColorStop(1, 'rgba(0,0,0,0)');
          f.fillStyle = g;
          f.fillRect(cx - R0, cy - R0, R0 * 2, R0 * 2);
        }
        if (age < 0.4 && bu.bolts > 0) {
          const RR = cardRR(bu.r),
            L = rrLen(RR),
            al = clamp((1 - age / 0.4) * bu.inten, 0, 1),
            seed = Math.floor(age * 30) * 13 + bu.seed,
            paths = [];
          for (let k = 0; k < bu.bolts; k++) {
            const pt = rrPoint(RR, hh(seed + k * 7.1) * L);
            jagLine(
              paths,
              pt.x,
              pt.y,
              pt.nx,
              pt.ny,
              v.releaseMargin * (0.6 + 0.4 * hh(seed + k)),
              6,
              5,
              seed + k * 11,
            );
          }
          f.lineJoin = 'round';
          f.strokeStyle = rgba(col.releaseBoltColor, al * 0.5);
          f.lineWidth = 5;
          f.shadowColor = rgba(col.releaseBoltColor, al);
          f.shadowBlur = 14 * DPR;
          paths.forEach((pp) => strokePoly(f, pp));
          f.shadowBlur = 0;
          f.strokeStyle = rgba([255, 255, 255], al);
          f.lineWidth = 1.4;
          paths.forEach((pp) => strokePoly(f, pp));
        }
        drawParts(f, bu.parts, bu.r, v.releaseMargin);
        f.restore();
      }
      f.save();
      clipStrip(f);
      f.globalCompositeOperation = 'lighter';
      drawParts(f, this.trail, null, 0);
      f.restore();
      this._drawRocket(f);
      if (this.debugBounds) {
        f.save();
        f.setLineDash([4, 4]);
        f.lineWidth = 1;
        f.strokeStyle = 'rgba(255,80,90,.9)';
        f.beginPath();
        f.moveTo(0, STRIP);
        f.lineTo(SW, STRIP);
        f.stroke();
        f.strokeStyle = 'rgba(255,255,255,.35)';
        for (const a of this.cards) {
          const C = a.clip;
          if (C) {
            rr(f, C.x, C.y, C.w, C.h, C.r);
            f.stroke();
          }
        }
        f.restore();
      }
      const row = this.shakeEl;
      if (row) {
        if (this.shakeAmp > 0.05) {
          row.style.transform = `translate(${((Math.random() - 0.5) * 2 * this.shakeAmp).toFixed(2)}px,${((Math.random() - 0.5) * 2 * this.shakeAmp).toFixed(2)}px)`;
          this.shakeAmp *= Math.exp(-rdt * 9);
        } else if (row.style.transform) {
          row.style.transform = '';
          this.shakeAmp = 0;
        }
      }
    }
  }

  /* ===================== public API ===================== */
  const API = {
    createStage: (o) => new Stage(o),
    registerPackage,
    getPackage,
    packages: () => Object.values(REGISTRY),
    defaults: DEFAULTS,
    compile: (x, type) => compileAs(x, type || 'vibe'),
    energy,
    flip,
    easings: EASES,
    shapes: {
      list: () => SHAPE_DEFS.slice(),
      groups: SHAPE_GROUPS.slice(),
      info: shapeInfo,
      register: registerShape,
      used: shapesUsed,
      /* Draw a shape into a canvas (for pickers and previews). */
      thumb(canvas, id, color) {
        const g = canvas.getContext('2d'),
          W = canvas.width;
        g.clearRect(0, 0, W, W);
        const c = color ? hexToRgb(color) : [255, 231, 141];
        if (id === 'spark') {
          g.strokeStyle = rgba(c, 1);
          g.lineWidth = W * 0.07;
          g.lineCap = 'round';
          g.beginPath();
          g.moveTo(W * 0.25, W * 0.75);
          g.lineTo(W * 0.75, W * 0.25);
          g.stroke();
          g.fillStyle = '#fff';
          g.beginPath();
          g.arc(W * 0.75, W * 0.25, W * 0.07, 0, TAU);
          g.fill();
          return true;
        }
        if (id === 'puffs') {
          g.drawImage(sprite([255, 140, 60]), W * 0.1, W * 0.1, W * 0.8, W * 0.8);
          return true;
        }
        const d = shapeInfo(id);
        if (!d) return false;
        const cv =
          d.kind === 'white'
            ? shapeTinted(
                id,
                d.multi === 'fall' ? FALL_PAL[0] : d.multi ? [255, 143, 184] : c,
                'in',
              )
            : shapeBase(id);
        if (!cv) return false;
        g.drawImage(cv, W * 0.08, W * 0.08, W * 0.84, W * 0.84);
        return true;
      },
      renderSVG,
    },
    _internal: {
      hexToRgb,
      mix,
      rgba,
      sprite,
      setDefaults(d) {
        if (d) for (const t of TYPES) DEFAULTS[t] = d[t] || {};
      },
    },
  };
  return API;
})();

export default SongShakeFX;
